import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { transportAPI, driverAPI, hostelInventoryAPI, transportODOAPI } from '../../services/api';
import HostelUserPortalTab from '../hostel/HostelUserPortalTab';
import LeaveManagementSidebar from '../leave/LeaveManagementSidebar';
import ApproveLeaves from '../leave/ApproveLeaves';
import TransportRoutesTab from '../transport/TransportRoutesTab';
import TransportFeeConfigTab from '../transport/TransportFeeConfigTab';
import TransportVehicleExpensesTab from '../transport/TransportVehicleExpensesTab';
import Calendar from '../shared/Calendar';
import { notify, confirmDialog } from '../../utils/notify';
import '../../styles/Dashboard.css';
import '../../styles/TransportDashboard.css';

const VEHICLE_TYPE_OPTIONS = [
  { value: 'BUS', label: 'Bus' },
  { value: 'VAN', label: 'Van' },
  { value: 'CAR', label: 'Car' },
  { value: 'AMBULANCE', label: 'Ambulance' },
  { value: 'OTHER', label: 'Other' },
];

const VEHICLE_STATUS_OPTIONS = [
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'ACTIVE', label: 'Active / In Service' },
  { value: 'UNDER_MAINTENANCE', label: 'Under Maintenance' },
  { value: 'OUT_OF_SERVICE', label: 'Out of Service' },
  { value: 'RETIRED', label: 'Retired' },
];

const getDocumentStatusInfo = (dateStr) => {
  if (!dateStr) {
    return { status: 'NOT_PROVIDED', label: '⚪ Expiry Date Not Set', color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', daysLeft: null };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = new Date(dateStr);
  expDate.setHours(0, 0, 0, 0);
  const diffTime = expDate - today;
  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) {
    const ago = Math.abs(daysLeft);
    return {
      status: 'EXPIRED',
      label: `🚨 Expired ${ago === 1 ? '1 day' : `${ago} days`} ago`,
      color: '#991b1b',
      bg: '#fef2f2',
      border: '#fecaca',
      daysLeft
    };
  } else if (daysLeft <= 30) {
    return {
      status: 'EXPIRING_SOON',
      label: `⚠️ Expiring in ${daysLeft === 0 ? 'today' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`}`,
      color: '#b45309',
      bg: '#fffbeb',
      border: '#fde68a',
      daysLeft
    };
  } else {
    return {
      status: 'VALID',
      label: `✓ Valid (${daysLeft} days left)`,
      color: '#15803d',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      daysLeft
    };
  }
};

const getVehicleOverallDocumentStatus = (vehicle) => {
  const docs = [
    vehicle.registration_expiry,
    vehicle.insurance_expiry,
    vehicle.pollution_expiry,
    vehicle.permit_expiry
  ].map(getDocumentStatusInfo);

  if (docs.some(d => d.status === 'EXPIRED')) return 'EXPIRED';
  if (docs.some(d => d.status === 'EXPIRING_SOON')) return 'EXPIRING_SOON';
  return 'VALID';
};

const getStatusBadge = (status) => {
  const map = {
    AVAILABLE: { label: 'Available', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    ACTIVE: { label: 'Active / In Service', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    UNDER_MAINTENANCE: { label: 'Under Maintenance', bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
    OUT_OF_SERVICE: { label: 'Out of Service', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    RETIRED: { label: 'Retired', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  };
  const b = map[status] || { label: status || 'Unknown', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: b.bg, color: b.color, border: `1px solid ${b.border}` }}>
      {b.label}
    </span>
  );
};

// ─── IST (Indian Standard Time, UTC+5:30) Date Utilities ───
const getCurrentISTDateString = () => {
  const now = new Date();
  const utcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(utcMs + istOffsetMs);
  return istDate.toISOString().split('T')[0];
};

const getFormattedISTDateString = () => {
  const now = new Date();
  const utcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(utcMs + istOffsetMs);
  return istDate.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getMsUntilNextMidnightIST = () => {
  const now = new Date();
  const utcMs = now.getTime() + (now.getTimezoneOffset() * 60000);
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const istNow = new Date(utcMs + istOffsetMs);

  const nextMidnightIST = new Date(istNow);
  nextMidnightIST.setHours(24, 0, 0, 0); // Next 12:00:00 AM IST

  return Math.max(1000, nextMidnightIST.getTime() - istNow.getTime());
};

const TransportDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('vehicles');
  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['vehicles', 'drivers', 'routes', 'fee_config', 'driver_leaves', 'my_leave', 'hostel'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Stats and Data State
  const [stats, setStats] = useState({
    total_vehicles: 0,
    active_vehicles: 0,
    under_maintenance: 0,
    out_of_service: 0,
    retired: 0,
    expired_documents: 0,
    expiring_soon_documents: 0,
  });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Vehicle Filters and Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [complianceFilter, setComplianceFilter] = useState('ALL');
  const [assignmentFilter, setAssignmentFilter] = useState('ALL');

  // Vehicle Modal States
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Vehicle Form State
  const [formData, setFormData] = useState({
    registration_number: '',
    vehicle_type: 'BUS',
    make: '',
    model: '',
    manufacturing_year: new Date().getFullYear(),
    seating_capacity: 40,
    status: 'AVAILABLE',
    registration_expiry: '',
    insurance_expiry: '',
    pollution_expiry: '',
    permit_expiry: '',
  });
  const [docFiles, setDocFiles] = useState({
    registration_document: null,
    insurance_document: null,
    pollution_document: null,
    permit_document: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);

  // ─── DRIVER MANAGEMENT STATE ───
  const [drivers, setDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [driverEligibilityFilter, setDriverEligibilityFilter] = useState('ALL');
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Driver Verification Modal State
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedDriverProfile, setSelectedDriverProfile] = useState(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');
  const [permittedTypesInput, setPermittedTypesInput] = useState([]);

  // Driver Assignment Modal State
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTargetVehicle, setAssignTargetVehicle] = useState(null);
  const [eligibleDrivers, setEligibleDrivers] = useState([]);
  const [loadingEligibleDrivers, setLoadingEligibleDrivers] = useState(false);
  const [selectedDriverToAssign, setSelectedDriverToAssign] = useState('');

  // ─── ODO METER TRACKING STATE ───
  const [dailyODOVehicles, setDailyODOVehicles] = useState([]);
  const [loadingDailyODO, setLoadingDailyODO] = useState(false);
  const [odoReports, setOdoReports] = useState(null);
  const [activeISTDate, setActiveISTDate] = useState(getCurrentISTDateString());

  // ODO Vehicle Details Modal State
  const [odoDetailTab, setOdoDetailTab] = useState('specs'); // 'specs' | 'odo'
  const [odoHistoryData, setOdoHistoryData] = useState(null);
  const [loadingOdoHistory, setLoadingOdoHistory] = useState(false);
  const [odoHistoryMovementFilter, setOdoHistoryMovementFilter] = useState('ALL');
  const [odoFilterFromDate, setOdoFilterFromDate] = useState('');
  const [odoFilterToDate, setOdoFilterToDate] = useState('');

  // Departure ODO Modal State
  const [showDepartureModal, setShowDepartureModal] = useState(false);
  const [departureTargetVehicle, setDepartureTargetVehicle] = useState(null);
  const [departureOdoInput, setDepartureOdoInput] = useState('');
  const [departureRemarksInput, setDepartureRemarksInput] = useState('');
  const [submittingDeparture, setSubmittingDeparture] = useState(false);
  const [discrepancyAlertData, setDiscrepancyAlertData] = useState(null);

  // Arrival ODO Modal State
  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [arrivalTargetVehicle, setArrivalTargetVehicle] = useState(null);
  const [arrivalOdoInput, setArrivalOdoInput] = useState('');
  const [arrivalRemarksInput, setArrivalRemarksInput] = useState('');
  const [submittingArrival, setSubmittingArrival] = useState(false);

  // Interim Movement Modal State
  const [showInterimModal, setShowInterimModal] = useState(false);
  const [interimTargetVehicle, setInterimTargetVehicle] = useState(null);
  const [interimOdoInput, setInterimOdoInput] = useState('');
  const [interimReasonChoice, setInterimReasonChoice] = useState('VEHICLE_RELOCATION');
  const [interimRemarksInput, setInterimRemarksInput] = useState('');
  const [interimDocFile, setInterimDocFile] = useState(null);
  const [submittingInterim, setSubmittingInterim] = useState(false);

  // ODO Correction Request Modal State (Transport Manager)
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionTargetVehicle, setCorrectionTargetVehicle] = useState(null);
  const [proposedOdoInput, setProposedOdoInput] = useState('');
  const [correctionReasonInput, setCorrectionReasonInput] = useState('');
  const [correctionDocFile, setCorrectionDocFile] = useState(null);
  const [submittingCorrection, setSubmittingCorrection] = useState(false);

  // Set Baseline ODO Modal State (Legacy / Uninitialized Vehicles)
  const [showBaselineModal, setShowBaselineModal] = useState(false);
  const [baselineTargetVehicle, setBaselineTargetVehicle] = useState(null);
  const [baselineOdoInput, setBaselineOdoInput] = useState('');
  const [baselineRemarksInput, setBaselineRemarksInput] = useState('');
  const [submittingBaseline, setSubmittingBaseline] = useState(false);

  // Admin Review Correction Requests State
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [loadingCorrections, setLoadingCorrections] = useState(false);
  const [selectedCorrectionForReview, setSelectedCorrectionForReview] = useState(null);
  const [reviewActionChoice, setReviewActionChoice] = useState('APPROVE');
  const [reviewRemarksInput, setReviewRemarksInput] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  // Check Hostel Allocation
  useEffect(() => {
    hostelInventoryAPI.getMyHostelProfile()
      .then(res => {
        if (res && res.allocated) {
          setHasHostelAllocation(true);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch Dashboard Stats and Vehicle List
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, vehiclesData] = await Promise.all([
        transportAPI.getStats(),
        transportAPI.getVehicles({
          search: searchQuery,
          vehicle_type: typeFilter !== 'ALL' ? typeFilter : undefined,
        }),
      ]);

      setStats(statsData);
      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : (vehiclesData.results || []));
    } catch (err) {
      console.error('Failed to load transport fleet data:', err);
      setError(err.response?.data?.error || 'Failed to load vehicle fleet data');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, typeFilter]);

  // Fetch Drivers List
  const fetchDriversData = useCallback(async () => {
    try {
      setLoadingDrivers(true);
      const data = await driverAPI.getDrivers({
        search: driverSearch,
        eligibility: driverEligibilityFilter !== 'ALL' ? driverEligibilityFilter : undefined,
      });
      setDrivers(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Failed to load drivers:', err);
    } finally {
      setLoadingDrivers(false);
    }
  }, [driverSearch, driverEligibilityFilter]);

  const fetchDailyODOStatus = useCallback(async () => {
    setLoadingDailyODO(true);
    try {
      const data = await transportODOAPI.getDailyStatus();
      setDailyODOVehicles(Array.isArray(data) ? data : data.results || []);
      const r = await transportODOAPI.getReports();
      setOdoReports(r);
    } catch (err) {
      console.error('Failed to fetch daily ODO status:', err);
    } finally {
      setLoadingDailyODO(false);
    }
  }, []);

  const fetchODOHistory = useCallback(async (vehicleId, movementFilter = 'ALL', fromDate = '', toDate = '') => {
    if (!vehicleId) return;
    setLoadingOdoHistory(true);
    try {
      const params = {};
      if (movementFilter && movementFilter !== 'ALL') params.movement_type = movementFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await transportODOAPI.getHistory(vehicleId, params);
      setOdoHistoryData(res);
    } catch (err) {
      console.error('Failed to fetch ODO history:', err);
    } finally {
      setLoadingOdoHistory(false);
    }
  }, []);

  const fetchCorrectionRequests = useCallback(async () => {
    setLoadingCorrections(true);
    try {
      const res = await transportODOAPI.getCorrectionRequests({ status: 'ALL' });
      setCorrectionRequests(Array.isArray(res) ? res : res.results || []);
    } catch (err) {
      console.error('Failed to fetch ODO correction requests:', err);
    } finally {
      setLoadingCorrections(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (activeTab === 'drivers') {
      fetchDriversData();
    } else if (activeTab === 'odo_tracking') {
      fetchDailyODOStatus();
    }
  }, [activeTab, fetchDriversData, fetchDailyODOStatus]);

  // ─── 12:00 AM IST AUTOMATIC DAILY RESET TIMER & HEARTBEAT ───
  useEffect(() => {
    let timeoutId;
    let heartbeatId;

    const scheduleMidnightReset = () => {
      const msUntilMidnight = getMsUntilNextMidnightIST();
      timeoutId = setTimeout(() => {
        // Clock reached 12:00 AM IST! Reset daily table
        const newISTDate = getCurrentISTDateString();
        setActiveISTDate(newISTDate);
        fetchDailyODOStatus();
        fetchDashboardData();
        // Schedule next midnight
        scheduleMidnightReset();
      }, msUntilMidnight);
    };

    scheduleMidnightReset();

    // Heartbeat check every 30 seconds (handles system sleep / laptop closure / background tab resume)
    let lastCheckedISTDate = getCurrentISTDateString();
    heartbeatId = setInterval(() => {
      const currentIST = getCurrentISTDateString();
      if (currentIST !== lastCheckedISTDate) {
        lastCheckedISTDate = currentIST;
        setActiveISTDate(currentIST);
        fetchDailyODOStatus();
        fetchDashboardData();
      }
    }, 30000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (heartbeatId) clearInterval(heartbeatId);
    };
  }, [fetchDailyODOStatus, fetchDashboardData]);

  // Open Vehicle Form Modal for Create or Edit
  const handleOpenForm = (vehicle = null) => {
    setFormErrors({});
    setDocFiles({
      registration_document: null,
      insurance_document: null,
      pollution_document: null,
      permit_document: null,
    });
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        registration_number: vehicle.registration_number || '',
        vehicle_type: vehicle.vehicle_type || 'BUS',
        make: vehicle.make || '',
        model: vehicle.model || '',
        manufacturing_year: vehicle.manufacturing_year || new Date().getFullYear(),
        seating_capacity: vehicle.seating_capacity || 10,
        initial_odo: vehicle.initial_odo !== undefined && vehicle.initial_odo !== null ? vehicle.initial_odo : 0,
        odometer_reading: vehicle.odometer_reading !== undefined && vehicle.odometer_reading !== null ? vehicle.odometer_reading : (vehicle.odo_meter_reading !== undefined && vehicle.odo_meter_reading !== null ? vehicle.odo_meter_reading : 0),
        status: vehicle.status || 'AVAILABLE',
        registration_expiry: vehicle.registration_expiry || '',
        insurance_expiry: vehicle.insurance_expiry || '',
        pollution_expiry: vehicle.pollution_expiry || '',
        permit_expiry: vehicle.permit_expiry || '',
      });
    } else {
      setEditingVehicle(null);
      setFormData({
        registration_number: '',
        vehicle_type: 'BUS',
        make: '',
        model: '',
        manufacturing_year: new Date().getFullYear(),
        seating_capacity: 40,
        initial_odo: '',
        odometer_reading: '',
        status: 'AVAILABLE',
        registration_expiry: '',
        insurance_expiry: '',
        pollution_expiry: '',
        permit_expiry: '',
      });
    }
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setShowFormModal(false);
    setEditingVehicle(null);
    setFormErrors({});
    setDocFiles({
      registration_document: null,
      insurance_document: null,
      pollution_document: null,
      permit_document: null,
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.registration_number.trim()) {
      errors.registration_number = 'Registration number is required.';
    }
    if (!formData.make.trim()) {
      errors.make = 'Make / Manufacturer is required.';
    }
    if (!formData.model.trim()) {
      errors.model = 'Model is required.';
    }
    const currentYear = new Date().getFullYear();
    if (!formData.manufacturing_year || formData.manufacturing_year < 1980 || formData.manufacturing_year > currentYear + 1) {
      errors.manufacturing_year = `Manufacturing year must be between 1980 and ${currentYear + 1}.`;
    }
    if (!formData.seating_capacity || parseInt(formData.seating_capacity, 10) <= 0) {
      errors.seating_capacity = 'Seating capacity must be greater than 0.';
    }
    if (!editingVehicle || !editingVehicle.initial_odo || editingVehicle.initial_odo === 0) {
      const initVal = formData.initial_odo !== '' && formData.initial_odo !== null ? parseInt(formData.initial_odo, 10) : NaN;
      if (isNaN(initVal)) {
        errors.initial_odo = 'Initial ODO Meter Reading is required and must be numeric.';
      } else if (initVal < 0) {
        errors.initial_odo = 'Initial ODO Meter Reading cannot be negative.';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setFormSubmitting(true);
      setSuccessMsg(null);
      setError(null);

      const formDataObj = new FormData();
      formDataObj.append('registration_number', formData.registration_number.toUpperCase());
      formDataObj.append('vehicle_type', formData.vehicle_type);
      formDataObj.append('make', formData.make);
      formDataObj.append('model', formData.model);
      formDataObj.append('manufacturing_year', parseInt(formData.manufacturing_year, 10));
      formDataObj.append('seating_capacity', parseInt(formData.seating_capacity, 10));
      if ((!editingVehicle || !editingVehicle.initial_odo || editingVehicle.initial_odo === 0) && formData.initial_odo !== '' && formData.initial_odo !== null) {
        const initVal = parseInt(formData.initial_odo, 10) || 0;
        formDataObj.append('initial_odo', initVal);
        formDataObj.append('odometer_reading', initVal);
      }
      
      if (editingVehicle && formData.status) {
        formDataObj.append('status', formData.status);
      }
      if (formData.registration_expiry) formDataObj.append('registration_expiry', formData.registration_expiry);
      if (formData.insurance_expiry) formDataObj.append('insurance_expiry', formData.insurance_expiry);
      if (formData.pollution_expiry) formDataObj.append('pollution_expiry', formData.pollution_expiry);
      if (formData.permit_expiry) formDataObj.append('permit_expiry', formData.permit_expiry);

      // Append file uploads if selected
      if (docFiles.registration_document) formDataObj.append('registration_document', docFiles.registration_document);
      if (docFiles.insurance_document) formDataObj.append('insurance_document', docFiles.insurance_document);
      if (docFiles.pollution_document) formDataObj.append('pollution_document', docFiles.pollution_document);
      if (docFiles.permit_document) formDataObj.append('permit_document', docFiles.permit_document);

      if (editingVehicle) {
        await transportAPI.updateVehicle(editingVehicle.id, formDataObj);
        setSuccessMsg(`Vehicle ${formData.registration_number} updated successfully.`);
      } else {
        await transportAPI.createVehicle(formDataObj);
        setSuccessMsg(`Vehicle ${formData.registration_number} added to fleet successfully.`);
      }

      handleCloseForm();
      fetchDashboardData();
    } catch (err) {
      console.error('Failed to save vehicle:', err);
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const fieldErrors = {};
        Object.keys(data).forEach(k => {
          fieldErrors[k] = Array.isArray(data[k]) ? data[k].join(' ') : data[k];
        });
        setFormErrors(fieldErrors);
      } else {
        setError(data?.error || 'Failed to save vehicle record.');
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleViewDetail = (vehicle) => {
    setSelectedVehicle(vehicle);
    setOdoDetailTab('specs');
    setOdoFilterFromDate('');
    setOdoFilterToDate('');
    setOdoHistoryMovementFilter('ALL');
    setShowDetailModal(true);
    fetchODOHistory(vehicle.id, 'ALL', '', '');
  };

  // Departure ODO Handlers
  const handleOpenDepartureModal = (vehicle) => {
    setDepartureTargetVehicle(vehicle);
    setDepartureOdoInput(vehicle.odometer_reading || vehicle.odo_meter_reading || '');
    setDepartureRemarksInput('');
    setDiscrepancyAlertData(null);
    setShowDepartureModal(true);
  };

  const handleRecordDepartureSubmit = async (e) => {
    e.preventDefault();
    if (departureOdoInput === '' || parseInt(departureOdoInput, 10) < 0) {
      notify.error('Please enter a valid non-negative Departure ODO reading.');
      return;
    }
    setSubmittingDeparture(true);
    setError(null);
    try {
      const res = await transportODOAPI.recordDeparture({
        vehicle_id: departureTargetVehicle.id,
        departure_odo: parseInt(departureOdoInput, 10),
        remarks: departureRemarksInput
      });

      if (res.discrepancy_detected) {
        setDiscrepancyAlertData(res);
        fetchDailyODOStatus();
      } else {
        setSuccessMsg(res.message);
        setShowDepartureModal(false);
        setDepartureTargetVehicle(null);
        fetchDashboardData();
        fetchDailyODOStatus();
        setTimeout(() => setSuccessMsg(null), 4000);
      }
    } catch (err) {
      console.error('Departure ODO error:', err);
      notify.error(err.response?.data?.error || 'Failed to record departure ODO.');
    } finally {
      setSubmittingDeparture(false);
    }
  };

  // Arrival ODO Handlers
  const handleOpenArrivalModal = (vehicle) => {
    setArrivalTargetVehicle(vehicle);
    const depOdo = vehicle.daily_odo_status?.today_departure_odo || vehicle.odometer_reading || vehicle.odo_meter_reading || 0;
    setArrivalOdoInput(depOdo);
    setArrivalRemarksInput('');
    setShowArrivalModal(true);
  };

  const handleRecordArrivalSubmit = async (e) => {
    e.preventDefault();
    if (arrivalOdoInput === '' || parseInt(arrivalOdoInput, 10) < 0) {
      notify.error('Please enter a valid non-negative Arrival ODO reading.');
      return;
    }
    setSubmittingArrival(true);
    setError(null);
    try {
      const res = await transportODOAPI.recordArrival({
        vehicle_id: arrivalTargetVehicle.id,
        arrival_odo: parseInt(arrivalOdoInput, 10),
        remarks: arrivalRemarksInput
      });

      setSuccessMsg(res.message);
      setShowArrivalModal(false);
      setArrivalTargetVehicle(null);
      fetchDashboardData();
      fetchDailyODOStatus();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Arrival ODO error:', err);
      notify.error(err.response?.data?.error || 'Failed to record arrival ODO.');
    } finally {
      setSubmittingArrival(false);
    }
  };

  // Interim Movement Handlers
  const handleOpenInterimModal = (vehicle) => {
    setInterimTargetVehicle(vehicle);
    setInterimOdoInput(vehicle.odometer_reading || vehicle.odo_meter_reading || '');
    setInterimReasonChoice('VEHICLE_RELOCATION');
    setInterimRemarksInput('');
    setInterimDocFile(null);
    setShowInterimModal(true);
  };

  const handleRecordInterimSubmit = async (e) => {
    e.preventDefault();
    if (interimOdoInput === '' || parseInt(interimOdoInput, 10) < 0) {
      notify.error('Please enter a valid non-negative Interim ODO reading.');
      return;
    }
    if (interimReasonChoice === 'OTHER' && !interimRemarksInput.trim()) {
      notify.error('Please enter description/remarks for selecting Other reason.');
      return;
    }

    setSubmittingInterim(true);
    setError(null);
    try {
      const formDataObj = new FormData();
      formDataObj.append('vehicle_id', interimTargetVehicle.id);
      formDataObj.append('odo_reading', parseInt(interimOdoInput, 10));
      formDataObj.append('interim_reason', interimReasonChoice);
      formDataObj.append('remarks', interimRemarksInput);
      if (interimDocFile) formDataObj.append('supporting_document', interimDocFile);

      const res = await transportODOAPI.recordInterim(formDataObj);
      setSuccessMsg(res.message);
      setShowInterimModal(false);
      setInterimTargetVehicle(null);
      fetchDashboardData();
      fetchDailyODOStatus();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Interim ODO error:', err);
      notify.error(err.response?.data?.error || 'Failed to record interim movement.');
    } finally {
      setSubmittingInterim(false);
    }
  };

  // Correction Request Handlers
  const handleOpenCorrectionModal = (vehicle) => {
    setCorrectionTargetVehicle(vehicle);
    setProposedOdoInput(vehicle.odometer_reading || vehicle.odo_meter_reading || '');
    setCorrectionReasonInput('');
    setCorrectionDocFile(null);
    setShowCorrectionModal(true);
  };

  const handleSubmitCorrectionSubmit = async (e) => {
    e.preventDefault();
    if (proposedOdoInput === '' || parseInt(proposedOdoInput, 10) < 0) {
      notify.error('Please enter a valid proposed ODO reading.');
      return;
    }
    if (!correctionReasonInput.trim()) {
      notify.error('Please enter a mandatory detailed reason for the ODO correction request.');
      return;
    }

    setSubmittingCorrection(true);
    setError(null);
    try {
      const formDataObj = new FormData();
      formDataObj.append('vehicle_id', correctionTargetVehicle.id);
      formDataObj.append('proposed_odo_reading', parseInt(proposedOdoInput, 10));
      formDataObj.append('correction_reason', correctionReasonInput);
      if (correctionDocFile) formDataObj.append('supporting_document', correctionDocFile);

      const res = await transportODOAPI.submitCorrectionRequest(formDataObj);
      setSuccessMsg(res.message);
      setShowCorrectionModal(false);
      setCorrectionTargetVehicle(null);
      fetchCorrectionRequests();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Correction request error:', err);
      notify.error(err.response?.data?.error || 'Failed to submit ODO correction request.');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  // Set Baseline ODO Handlers
  const handleOpenBaselineModal = (vehicle) => {
    setBaselineTargetVehicle(vehicle);
    setBaselineOdoInput(vehicle.initial_odo || vehicle.odometer_reading || '');
    setBaselineRemarksInput('');
    setShowBaselineModal(true);
  };

  const handleSetBaselineSubmit = async (e) => {
    e.preventDefault();
    if (!baselineTargetVehicle) return;
    const initVal = parseInt(baselineOdoInput, 10);
    if (isNaN(initVal) || initVal < 0) {
      notify.error('Please enter a valid non-negative starting baseline ODO reading.');
      return;
    }
    try {
      setSubmittingBaseline(true);
      setError(null);
      const res = await transportODOAPI.initializeBaseline({
        vehicle_id: baselineTargetVehicle.id,
        initial_odo: initVal,
        remarks: baselineRemarksInput || 'Initial baseline ODO configured by Transport Manager'
      });
      setSuccessMsg(res.message || `Baseline ODO initialized to ${initVal.toLocaleString()} KM for ${baselineTargetVehicle.registration_number}.`);
      setShowBaselineModal(false);
      setBaselineTargetVehicle(null);
      fetchDailyODOStatus();
      fetchDashboardData();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Baseline ODO error:', err);
      notify.error(err.response?.data?.error || 'Failed to initialize baseline ODO.');
    } finally {
      setSubmittingBaseline(false);
    }
  };

  // Admin Review Handlers
  const handleOpenReviewCorrectionModal = (reqObj) => {
    setSelectedCorrectionForReview(reqObj);
    setReviewActionChoice('APPROVE');
    setReviewRemarksInput('');
  };

  const handleReviewCorrectionSubmit = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      const res = await transportODOAPI.reviewCorrectionRequest(selectedCorrectionForReview.id, {
        action: reviewActionChoice,
        review_remarks: reviewRemarksInput
      });
      setSuccessMsg(res.message);
      setSelectedCorrectionForReview(null);
      fetchCorrectionRequests();
      fetchDashboardData();
      fetchDailyODOStatus();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Review correction error:', err);
      notify.error(err.response?.data?.error || 'Failed to process correction review.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // ─── DRIVER VERIFICATION HANDLERS ───
  const handleOpenVerifyModal = (driverProfile) => {
    setSelectedDriverProfile(driverProfile);
    setRejectionReasonInput('');
    setPermittedTypesInput(driverProfile.permitted_vehicle_types || []);
    setShowVerifyModal(true);
  };

  const handleVerifyDocument = async (docType, action) => {
    if (!selectedDriverProfile) return;
    try {
      setError(null);
      setSuccessMsg(null);
      const res = await driverAPI.verifyDocument(selectedDriverProfile.user_id, {
        doc_type: docType,
        action: action,
        rejection_reason: action === 'REJECT' ? rejectionReasonInput : '',
        permitted_vehicle_types: permittedTypesInput,
      });

      setSuccessMsg(res.message);
      setSelectedDriverProfile(res.profile);
      fetchDriversData();
    } catch (err) {
      console.error('Document verification failed:', err);
      setError(err.response?.data?.error || 'Document verification failed.');
    }
  };

  // ─── DRIVER ASSIGNMENT HANDLERS ───
  const handleOpenAssignModal = async (vehicle) => {
    setAssignTargetVehicle(vehicle);
    setSelectedDriverToAssign('');
    setShowAssignModal(true);
    try {
      setLoadingEligibleDrivers(true);
      const res = await driverAPI.getEligibleDriversForVehicle(vehicle.id);
      setEligibleDrivers(res.eligible_drivers || []);
    } catch (err) {
      console.error('Failed to fetch eligible drivers:', err);
      setError('Failed to fetch eligible drivers for vehicle.');
    } finally {
      setLoadingEligibleDrivers(false);
    }
  };

  const handleConfirmAssignment = async () => {
    if (!assignTargetVehicle || !selectedDriverToAssign) return;
    try {
      setError(null);
      setSuccessMsg(null);
      const res = await driverAPI.assignDriver(assignTargetVehicle.id, selectedDriverToAssign);
      setSuccessMsg(res.message);
      setShowAssignModal(false);
      fetchDashboardData();
    } catch (err) {
      console.error('Failed to assign driver:', err);
      setError(err.response?.data?.error || 'Failed to assign driver to vehicle.');
    }
  };

  const handleUnassignDriver = async (vehicleId) => {
    if (!await confirmDialog('Are you sure you want to unassign the current driver from this vehicle?')) return;
    try {
      setError(null);
      setSuccessMsg(null);
      const res = await driverAPI.unassignDriver(vehicleId);
      setSuccessMsg(res.message);
      fetchDashboardData();
    } catch (err) {
      console.error('Failed to unassign driver:', err);
      setError(err.response?.data?.error || 'Failed to unassign driver.');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="badge badge-green">Active / In Service</span>;
      case 'AVAILABLE':
        return <span className="badge badge-blue">Available</span>;
      case 'UNDER_MAINTENANCE':
        return <span className="badge badge-yellow">Under Maintenance</span>;
      case 'OUT_OF_SERVICE':
        return <span className="badge badge-orange">Out of Service</span>;
      case 'RETIRED':
        return <span className="badge badge-red">Retired</span>;
      default:
        return <span className="badge badge-gray">{status}</span>;
    }
  };

  return (
    <DashboardLayout fullWidth>
      <div className="tm-container">

        {/* Global Notifications */}
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: 20, borderRadius: 12, padding: '14px 18px', background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>⚠️</span> <span>{error}</span>
          </div>
        )}
        {successMsg && (
          <div className="alert alert-success" style={{ marginBottom: 20, borderRadius: 12, padding: '14px 18px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>✓</span> <span>{successMsg}</span>
          </div>
        )}

        {/* Segmented Nav Tabs */}
        <div className="tm-nav-tabs">
          <button
            className={`tm-tab-btn ${activeTab === 'vehicles' ? 'active' : ''}`}
            onClick={() => setActiveTab('vehicles')}
          >
            <span>🚘</span> Vehicle Fleet
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'odo_tracking' ? 'active' : ''}`}
            onClick={() => setActiveTab('odo_tracking')}
          >
            <span>📅</span> Daily ODO Tracking
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'drivers' ? 'active' : ''}`}
            onClick={() => setActiveTab('drivers')}
          >
            <span>🧑‍✈️</span> Driver Management
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'routes' ? 'active' : ''}`}
            onClick={() => setActiveTab('routes')}
          >
            <span>🗺️</span> Routes & Stops
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'fee_config' ? 'active' : ''}`}
            onClick={() => setActiveTab('fee_config')}
          >
            <span>💰</span> Fee Configuration
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'expenses' ? 'active' : ''}`}
            onClick={() => setActiveTab('expenses')}
          >
            <span>🔧</span> Maintenance & Expenses
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'driver_leaves' ? 'active' : ''}`}
            onClick={() => setActiveTab('driver_leaves')}
          >
            <span>📋</span> Driver Leave Requests
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'my_leave' ? 'active' : ''}`}
            onClick={() => setActiveTab('my_leave')}
          >
            <span>🌴</span> My Leave
          </button>
          <button
            className={`tm-tab-btn ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            <span>📅</span> Calendar
          </button>
          {hasHostelAllocation && (
            <button
              className={`tm-tab-btn ${activeTab === 'hostel' ? 'active' : ''}`}
              onClick={() => setActiveTab('hostel')}
            >
              <span>🏠</span> My Hostel
            </button>
          )}
        </div>

        {/* ─── DAILY ODO TRACKING TAB VIEW ─── */}
        {activeTab === 'odo_tracking' && (
          <div>
            {/* ODO Reports Overview Cards */}
            {odoReports && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Fleet Active Vehicles</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>{odoReports.total_vehicles}</div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>Today's Route KM</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#1d4ed8', marginTop: 4 }}>{odoReports.today_route_km?.toLocaleString()} KM</div>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase' }}>Total Interim KM</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a', marginTop: 4 }}>{odoReports.total_interim_km?.toLocaleString()} KM</div>
                </div>
                <div style={{ background: (odoReports.discrepancies_count + odoReports.suspicious_count) > 0 ? '#fef2f2' : '#f8fafc', border: (odoReports.discrepancies_count + odoReports.suspicious_count) > 0 ? '1px solid #fecaca' : '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: (odoReports.discrepancies_count + odoReports.suspicious_count) > 0 ? '#991b1b' : '#64748b', textTransform: 'uppercase' }}>Discrepancies / Suspicious</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: (odoReports.discrepancies_count + odoReports.suspicious_count) > 0 ? '#dc2626' : '#0f172a', marginTop: 4 }}>
                    {odoReports.discrepancies_count + odoReports.suspicious_count}
                  </div>
                </div>
              </div>
            )}

            {/* Daily Vehicles ODO Status Table */}
            <div className="tm-table-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              {loadingDailyODO ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>⏳ Loading daily ODO tracking data...</div>
              ) : dailyODOVehicles.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No active vehicles found in fleet.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                        <th style={{ padding: '12px 16px' }}>Vehicle Reg / Type</th>
                        <th style={{ padding: '12px 16px' }}>Current ODO</th>
                        <th style={{ padding: '12px 16px' }}>Departure ODO</th>
                        <th style={{ padding: '12px 16px' }}>Arrival ODO</th>
                        <th style={{ padding: '12px 16px' }}>Daily Route KM</th>
                        <th style={{ padding: '12px 16px' }}>Today's Interim KM</th>
                        <th style={{ padding: '12px 16px' }}>Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyODOVehicles.map(v => {
                        const st = v.daily_odo_status || {};
                        const currentOdo = v.odometer_reading ?? v.odo_meter_reading ?? 0;
                        return (
                          <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <strong style={{ fontSize: 14, color: '#0f172a' }}>{v.registration_number}</strong>
                              <div style={{ fontSize: 11, color: '#64748b' }}>{v.vehicle_type_display || v.vehicle_type} &bull; {v.make} {v.model}</div>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {currentOdo > 0 ? (
                                <strong style={{ fontSize: 14, color: '#1d4ed8' }}>📈 {currentOdo.toLocaleString()} KM</strong>
                              ) : (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <strong style={{ fontSize: 14, color: '#64748b' }}>📈 0 KM</strong>
                                  <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                                    ⚠️ Baseline Not Set
                                  </span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {st.today_departure_odo ? (
                                <span style={{ fontWeight: 600, color: '#334155' }}>🛫 {st.today_departure_odo.toLocaleString()} KM</span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not Recorded</span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {st.today_arrival_odo ? (
                                <span style={{ fontWeight: 600, color: '#15803d' }}>🛬 {st.today_arrival_odo.toLocaleString()} KM</span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Pending Arrival</span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <strong style={{ color: st.today_route_km > 0 ? '#15803d' : '#64748b' }}>
                                {st.today_route_km ? `${st.today_route_km.toLocaleString()} KM` : '0 KM'}
                              </strong>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ color: st.today_interim_km > 0 ? '#d97706' : '#64748b' }}>
                                {st.today_interim_km ? `${st.today_interim_km.toLocaleString()} KM` : '0 KM'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {st.status === 'COMPLETED' ? (
                                <span className="badge badge-green">✓ Completed</span>
                              ) : st.status === 'DEPARTURE_RECORDED' ? (
                                <span className="badge badge-yellow">🛫 Departure Recorded</span>
                              ) : st.status === 'DISCREPANCY' ? (
                                <span className="badge badge-red">⚠️ Requires Verification</span>
                              ) : st.status === 'SUSPICIOUS' ? (
                                <span className="badge badge-orange">🚨 Suspicious Reading</span>
                              ) : (
                                <span className="badge badge-gray">Pending Departure</span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                {currentOdo === 0 && (!v.initial_odo || v.initial_odo === 0) && (
                                  <button
                                    onClick={() => handleOpenBaselineModal(v)}
                                    style={{ fontSize: 11, padding: '5px 9px', borderRadius: 6, border: '1.5px solid #7c3aed', background: '#f5f3ff', color: '#6d28d9', cursor: 'pointer', fontWeight: 700 }}
                                    title="Set starting baseline ODO for this vehicle"
                                  >
                                    ⚙️ Set Baseline
                                  </button>
                                )}
                                <button
                                  onClick={() => handleOpenDepartureModal(v)}
                                  className="tm-btn-secondary"
                                  style={{ fontSize: 11, padding: '5px 9px' }}
                                  title="Record Departure ODO"
                                >
                                  🛫 Departure
                                </button>
                                <button
                                  onClick={() => handleOpenArrivalModal(v)}
                                  className="tm-btn-primary"
                                  style={{ fontSize: 11, padding: '5px 9px' }}
                                  title="Record Arrival ODO"
                                >
                                  🛬 Arrival
                                </button>
                                <button
                                  onClick={() => handleOpenInterimModal(v)}
                                  style={{ fontSize: 11, padding: '5px 9px', borderRadius: 6, border: '1px solid #d97706', background: '#fffbeb', color: '#b45309', cursor: 'pointer', fontWeight: 600 }}
                                  title="Record Interim/Non-Route Movement"
                                >
                                  🚘 Interim
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ROUTES MANAGEMENT TAB VIEW */}
        {activeTab === 'routes' && <TransportRoutesTab />}

        {/* TRANSPORT FEE CONFIGURATION TAB VIEW */}
        {activeTab === 'fee_config' && <TransportFeeConfigTab />}

        {/* MAINTENANCE & EXPENSES TAB VIEW */}
        {activeTab === 'expenses' && <TransportVehicleExpensesTab />}

        {/* DRIVER LEAVE APPROVALS TAB VIEW */}
        {activeTab === 'driver_leaves' && (
          <div className="tm-card">
            <ApproveLeaves showDepartmentView={false} />
          </div>
        )}

        {/* MY LEAVE TAB VIEW */}
        {activeTab === 'my_leave' && (
          <LeaveManagementSidebar />
        )}

        {/* Hostel Tab View */}
        {activeTab === 'hostel' && <HostelUserPortalTab />}

        {/* CALENDAR TAB VIEW */}
        {activeTab === 'calendar' && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
            <Calendar />
          </div>
        )}

        {/* VEHICLES TAB VIEW */}
        {activeTab === 'vehicles' && (
          <>
            {/* Stats Summary Grid */}
            <div className="tm-stats-grid">
              <div className="tm-stat-card">
                <div className="tm-stat-icon-wrapper tm-stat-icon-blue">🚘</div>
                <div>
                  <div className="tm-stat-val">{stats.total_vehicles}</div>
                  <div className="tm-stat-label">Total Vehicles</div>
                </div>
              </div>
              <div className="tm-stat-card">
                <div className="tm-stat-icon-wrapper tm-stat-icon-green">✅</div>
                <div>
                  <div className="tm-stat-val">{stats.active_vehicles}</div>
                  <div className="tm-stat-label">Active / Available</div>
                </div>
              </div>
              <div className="tm-stat-card">
                <div className="tm-stat-icon-wrapper tm-stat-icon-orange">🛠️</div>
                <div>
                  <div className="tm-stat-val">{stats.under_maintenance}</div>
                  <div className="tm-stat-label">Under Maintenance</div>
                </div>
              </div>
            </div>

            {/* Vehicle Master Roster Card */}
            <div className="tm-card">
              <div className="tm-card-header">
                <div>
                  <h2 className="tm-card-title">Vehicle Master Roster</h2>
                  <div className="tm-card-subtitle">
                    Showing {vehicles.length} registered vehicle(s)
                  </div>
                </div>
                <button
                  className="tm-btn-primary"
                  onClick={() => handleOpenForm(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span>➕</span> Add New Vehicle
                </button>
              </div>

              {/* Toolbar & Filter Dropdowns */}
              <div className="tm-toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <input
                  type="search"
                  placeholder="🔍 Search reg no, make, model, driver..."
                  className="tm-search-input"
                  style={{ flex: '1 1 200px' }}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />

                <select
                  className="tm-filter-select"
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value)}
                >
                  <option value="ALL">All Vehicle Types</option>
                  {VEHICLE_TYPE_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>

                <select
                  className="tm-filter-select"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="ALL">All Vehicle Statuses</option>
                  {VEHICLE_STATUS_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>

                <select
                  className="tm-filter-select"
                  value={complianceFilter}
                  onChange={e => setComplianceFilter(e.target.value)}
                >
                  <option value="ALL">All Document Statuses</option>
                  <option value="EXPIRING_SOON">⚠️ Expiring Soon (Within 30 Days)</option>
                  <option value="EXPIRED">🚨 Expired Documents</option>
                  <option value="VALID">✓ Fully Valid Documents</option>
                </select>

                <select
                  className="tm-filter-select"
                  value={assignmentFilter}
                  onChange={e => setAssignmentFilter(e.target.value)}
                >
                  <option value="ALL">All Driver Statuses</option>
                  <option value="ASSIGNED">🧑‍✈️ Driver Assigned</option>
                  <option value="UNASSIGNED">Unassigned Vehicles</option>
                </select>

                {(searchQuery || typeFilter !== 'ALL' || statusFilter !== 'ALL' || complianceFilter !== 'ALL' || assignmentFilter !== 'ALL') && (
                  <button
                    className="tm-btn-action edit"
                    onClick={() => {
                      setSearchQuery('');
                      setTypeFilter('ALL');
                      setStatusFilter('ALL');
                      setComplianceFilter('ALL');
                      setAssignmentFilter('ALL');
                    }}
                  >
                    ✕ Clear Filters
                  </button>
                )}
              </div>

              {/* Table Data */}
              {loading ? (
                <div style={{ padding: 50, textAlign: 'center', color: '#64748b' }}>
                  Loading vehicle fleet...
                </div>
              ) : (
                (() => {
                  const filteredVehicles = vehicles.filter(v => {
                    if (searchQuery.trim()) {
                      const q = searchQuery.toLowerCase().trim();
                      const reg = (v.registration_number || '').toLowerCase();
                      const make = (v.make || '').toLowerCase();
                      const model = (v.model || '').toLowerCase();
                      const driverName = (v.assigned_driver?.name || v.assigned_driver?.driver_name || v.driver?.name || v.driver?.driver_name || '').toLowerCase();
                      if (!reg.includes(q) && !make.includes(q) && !model.includes(q) && !driverName.includes(q)) {
                        return false;
                      }
                    }
                    if (typeFilter !== 'ALL' && v.vehicle_type !== typeFilter) return false;
                    if (statusFilter !== 'ALL' && v.status !== statusFilter) return false;
                    if (complianceFilter !== 'ALL') {
                      const compStatus = getVehicleOverallDocumentStatus(v);
                      if (complianceFilter === 'EXPIRING_SOON' && compStatus !== 'EXPIRING_SOON') return false;
                      if (complianceFilter === 'EXPIRED' && compStatus !== 'EXPIRED') return false;
                      if (complianceFilter === 'VALID' && compStatus !== 'VALID') return false;
                    }
                    if (assignmentFilter !== 'ALL') {
                      const hasDriver = !!(v.assigned_driver || v.driver);
                      if (assignmentFilter === 'ASSIGNED' && !hasDriver) return false;
                      if (assignmentFilter === 'UNASSIGNED' && hasDriver) return false;
                    }
                    return true;
                  });

                  if (filteredVehicles.length === 0) {
                    return (
                      <div style={{ padding: 50, textAlign: 'center', color: '#94a3b8' }}>
                        No vehicles found matching your criteria.
                      </div>
                    );
                  }

                  return (
                    <div className="table-container" style={{ overflowX: 'auto' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Reg Number & Status</th>
                            <th>Type</th>
                            <th>Make / Model</th>
                            <th>Assigned Driver</th>
                            <th>Document Compliance</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredVehicles.map(v => {
                            const assignedDriver = v.assigned_driver || v.driver;
                            const overallStatus = getVehicleOverallDocumentStatus(v);
                            const pucStatus = getDocumentStatusInfo(v.pollution_expiry);
                            const insStatus = getDocumentStatusInfo(v.insurance_expiry);
                            const regStatus = getDocumentStatusInfo(v.registration_expiry);
                            const pmtStatus = getDocumentStatusInfo(v.permit_expiry);

                            return (
                              <tr key={v.id}>
                                <td>
                                  <strong style={{ fontSize: 14, color: '#0f172a', fontWeight: 800 }}>
                                    {v.registration_number}
                                  </strong>
                                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                    ID: {v.vehicle_id || `VEH-${v.id}`} &bull; {v.status || 'AVAILABLE'}
                                  </div>
                                </td>
                                <td>
                                  <strong style={{ fontSize: 13, color: '#0f172a' }}>
                                    {v.vehicle_type_display || v.vehicle_type}
                                  </strong>
                                </td>
                                <td>
                                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{v.make} {v.model}</div>
                                  <div style={{ fontSize: 11, color: '#64748b' }}>Year: {v.manufacturing_year} &bull; {v.seating_capacity} Seats &bull; 📈 ODO: {(v.odometer_reading !== undefined && v.odometer_reading !== null) ? v.odometer_reading : (v.odo_meter_reading ?? 0)} km</div>
                                </td>
                                <td>
                                  {assignedDriver ? (
                                    <div>
                                      <strong style={{ fontSize: 13, color: '#1e293b' }}>
                                        🧑‍✈️ {assignedDriver.name || assignedDriver.driver_name}
                                      </strong>
                                      <div style={{ fontSize: 11, color: '#64748b' }}>
                                        ID: {assignedDriver.employee_id || 'Assigned'}
                                      </div>
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                                      Unassigned
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {pucStatus.status === 'EXPIRING_SOON' && (
                                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: pucStatus.bg, color: pucStatus.color, border: `1px solid ${pucStatus.border}`, width: 'fit-content' }}>
                                        PUC: {pucStatus.label}
                                      </span>
                                    )}
                                    {insStatus.status === 'EXPIRING_SOON' && (
                                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: insStatus.bg, color: insStatus.color, border: `1px solid ${insStatus.border}`, width: 'fit-content' }}>
                                        Insurance: {insStatus.label}
                                      </span>
                                    )}
                                    {regStatus.status === 'EXPIRING_SOON' && (
                                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: regStatus.bg, color: regStatus.color, border: `1px solid ${regStatus.border}`, width: 'fit-content' }}>
                                        RC: {regStatus.label}
                                      </span>
                                    )}
                                    {pmtStatus.status === 'EXPIRING_SOON' && (
                                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: pmtStatus.bg, color: pmtStatus.color, border: `1px solid ${pmtStatus.border}`, width: 'fit-content' }}>
                                        Permit: {pmtStatus.label}
                                      </span>
                                    )}
                                    
                                    {overallStatus === 'EXPIRED' && (
                                      <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', width: 'fit-content' }}>
                                        🚨 Has Expired Documents
                                      </span>
                                    )}
                                    {overallStatus === 'VALID' && (
                                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', width: 'fit-content' }}>
                                        ✓ Documents Valid
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="tm-btn-group">
                                    <button
                                      className="tm-btn-action details"
                                      onClick={() => handleViewDetail(v)}
                                      title="View Specifications & Documents"
                                    >
                                      👁️ Details
                                    </button>
                                    <button
                                      className="tm-btn-action edit"
                                      onClick={() => handleOpenForm(v)}
                                      title="Edit Vehicle & Documents"
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      className="tm-btn-action assign"
                                      onClick={() => handleOpenAssignModal(v)}
                                      title={assignedDriver ? "View or Change Currently Assigned Driver" : "Assign Driver to Vehicle"}
                                    >
                                      {assignedDriver ? '✏️ Edit Driver' : '🧑‍✈️ Assign Driver'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              )}
            </div>
          </>
        )}

        {/* DRIVERS TAB VIEW */}
        {activeTab === 'drivers' && (
          <div className="tm-card">
            <div className="tm-card-header">
              <div>
                <h2 className="tm-card-title">Driver Management Roster</h2>
                <div className="tm-card-subtitle">
                  Review professional documentation, verification status, and vehicle eligibility.
                </div>
              </div>
            </div>

            {/* Driver Filters Toolbar */}
            <div className="tm-toolbar">
              <input
                type="search"
                placeholder="🔍 Search driver name, employee ID, licence..."
                className="tm-search-input"
                value={driverSearch}
                onChange={e => setDriverSearch(e.target.value)}
              />

              <select
                className="tm-filter-select"
                value={driverEligibilityFilter}
                onChange={e => setDriverEligibilityFilter(e.target.value)}
              >
                <option value="ALL">All Driver Eligibilities</option>
                <option value="ELIGIBLE">✓ Eligible for Vehicle Assignment</option>
                <option value="NOT_ELIGIBLE">✕ Not Eligible</option>
              </select>
            </div>

            {loadingDrivers ? (
              <div style={{ padding: 50, textAlign: 'center', color: '#64748b' }}>Loading Drivers...</div>
            ) : drivers.length === 0 ? (
              <div style={{ padding: 50, textAlign: 'center', color: '#94a3b8' }}>No driver records found.</div>
            ) : (
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Driver</th>
                      <th>Employee ID</th>
                      <th>Licence Status</th>
                      <th>Medical Status</th>
                      <th>Permitted Vehicles</th>
                      <th>Eligibility Status</th>
                      <th>Assigned Vehicle</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivers.map(d => (
                      <tr key={d.id}>
                        <td>
                          <strong style={{ color: '#0f172a' }}>{d.full_name || d.username}</strong>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{d.email || d.phone_number}</div>
                        </td>
                        <td>
                          <span className="badge badge-purple" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                            {d.employee_id || 'DRV260001'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${d.licence_verification_status === 'VERIFIED' ? 'badge-green' : d.licence_verification_status === 'REJECTED' ? 'badge-red' : 'badge-yellow'}`}>
                            {d.licence_verification_status}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${d.medical_verification_status === 'VERIFIED' ? 'badge-green' : d.medical_verification_status === 'REJECTED' ? 'badge-red' : 'badge-yellow'}`}>
                            {d.medical_verification_status}
                          </span>
                        </td>
                        <td>
                          {d.permitted_vehicle_types && d.permitted_vehicle_types.length > 0 ? (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {d.permitted_vehicle_types.map(pt => (
                                <span key={pt} className="badge badge-blue" style={{ fontSize: 10 }}>{pt}</span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>None</span>
                          )}
                        </td>
                        <td>
                          {d.is_eligible ? (
                            <span className="badge badge-green">✓ ELIGIBLE</span>
                          ) : (
                            <span className="badge badge-red">✕ NOT ELIGIBLE</span>
                          )}
                        </td>
                        <td>
                          {d.assigned_vehicle ? (
                            <div>
                              <strong style={{ fontSize: 13, color: '#0f172a' }}>
                                {d.assigned_vehicle.registration_number}
                              </strong>
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>Unassigned</span>
                          )}
                        </td>
                        <td>
                          <button
                            className="tm-btn-action details"
                            onClick={() => handleOpenVerifyModal(d)}
                          >
                            🔍 Review Documents
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── ADD / EDIT VEHICLE MODAL FORM ─── */}
        {showFormModal && (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="modal-content" style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', pb: 12, marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                  {editingVehicle ? `✏️ Edit Vehicle ${editingVehicle.registration_number}` : '➕ Add New Vehicle to Fleet'}
                </h3>
                <button onClick={handleCloseForm} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
              </div>

              <form onSubmit={handleFormSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      Registration Number *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. TN-01-AB-1234"
                      value={formData.registration_number}
                      onChange={e => setFormData({ ...formData, registration_number: e.target.value.toUpperCase() })}
                      style={{ width: '100%', padding: '8px 12px', border: formErrors.registration_number ? '1px solid #ef4444' : '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, textTransform: 'uppercase' }}
                    />
                    {formErrors.registration_number && <span style={{ color: '#ef4444', fontSize: 11 }}>{formErrors.registration_number}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      Vehicle Type *
                    </label>
                    <select
                      value={formData.vehicle_type}
                      onChange={e => setFormData({ ...formData, vehicle_type: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, background: '#fff' }}
                    >
                      {VEHICLE_TYPE_OPTIONS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      Make / Manufacturer *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Tata Motors, Force, Mahindra"
                      value={formData.make}
                      onChange={e => setFormData({ ...formData, make: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: formErrors.make ? '1px solid #ef4444' : '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                    />
                    {formErrors.make && <span style={{ color: '#ef4444', fontSize: 11 }}>{formErrors.make}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      Model *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Starbus, Traveller, Bolero"
                      value={formData.model}
                      onChange={e => setFormData({ ...formData, model: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: formErrors.model ? '1px solid #ef4444' : '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                    />
                    {formErrors.model && <span style={{ color: '#ef4444', fontSize: 11 }}>{formErrors.model}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      Manufacturing Year *
                    </label>
                    <input
                      type="number"
                      required
                      min="1980"
                      max={new Date().getFullYear() + 1}
                      value={formData.manufacturing_year}
                      onChange={e => setFormData({ ...formData, manufacturing_year: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: formErrors.manufacturing_year ? '1px solid #ef4444' : '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                    />
                    {formErrors.manufacturing_year && <span style={{ color: '#ef4444', fontSize: 11 }}>{formErrors.manufacturing_year}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      Seating Capacity *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      placeholder="e.g. 40"
                      value={formData.seating_capacity}
                      onChange={e => setFormData({ ...formData, seating_capacity: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: formErrors.seating_capacity ? '1px solid #ef4444' : '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                    />
                    {formErrors.seating_capacity && <span style={{ color: '#ef4444', fontSize: 11 }}>{formErrors.seating_capacity}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                      {editingVehicle && editingVehicle.initial_odo > 0 ? 'Initial ODO (KM) (Locked)' : 'Initial ODO Meter Reading (KM) *'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      disabled={editingVehicle && editingVehicle.initial_odo > 0}
                      required={!editingVehicle || !editingVehicle.initial_odo || editingVehicle.initial_odo === 0}
                      placeholder="e.g. 25000"
                      value={editingVehicle && editingVehicle.initial_odo > 0 ? (formData.initial_odo ?? formData.odometer_reading ?? 0) : formData.initial_odo}
                      onChange={e => setFormData({ ...formData, initial_odo: e.target.value, odometer_reading: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: formErrors.initial_odo ? '1px solid #ef4444' : '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, background: (editingVehicle && editingVehicle.initial_odo > 0) ? '#f1f5f9' : '#fff', color: (editingVehicle && editingVehicle.initial_odo > 0) ? '#64748b' : '#0f172a' }}
                    />
                    {formErrors.initial_odo && <span style={{ color: '#ef4444', fontSize: 11 }}>{formErrors.initial_odo}</span>}
                    {editingVehicle && editingVehicle.initial_odo > 0 && <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>🔒 Initial ODO cannot later be silently changed. Submit ODO Correction Request for adjustments.</div>}
                    {editingVehicle && (!editingVehicle.initial_odo || editingVehicle.initial_odo === 0) && <div style={{ fontSize: 11, color: '#d97706', marginTop: 3 }}>⚠️ Vehicle baseline ODO not yet set. Enter starting odometer reading.</div>}
                  </div>

                  {editingVehicle && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        Vehicle Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={e => setFormData({ ...formData, status: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, background: '#fff' }}
                      >
                        {VEHICLE_STATUS_OPTIONS.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                    📄 Statutory Document Expiry Dates & File Uploads
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {/* Registration Certificate (RC) */}
                    <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        Registration Expiry Date & RC File
                      </label>
                      <input
                        type="date"
                        value={formData.registration_expiry}
                        onChange={e => setFormData({ ...formData, registration_expiry: e.target.value })}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 8 }}
                      />
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={e => setDocFiles({ ...docFiles, registration_document: e.target.files[0] })}
                        style={{ fontSize: 11, width: '100%' }}
                      />
                      {editingVehicle?.documents_detail?.registration?.document_url && !docFiles.registration_document && (
                        <div style={{ marginTop: 4 }}>
                          <a href={editingVehicle.documents_detail.registration.document_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                            📄 Current Uploaded RC
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Insurance Policy */}
                    <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        Insurance Expiry Date & Policy File
                      </label>
                      <input
                        type="date"
                        value={formData.insurance_expiry}
                        onChange={e => setFormData({ ...formData, insurance_expiry: e.target.value })}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 8 }}
                      />
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={e => setDocFiles({ ...docFiles, insurance_document: e.target.files[0] })}
                        style={{ fontSize: 11, width: '100%' }}
                      />
                      {editingVehicle?.documents_detail?.insurance?.document_url && !docFiles.insurance_document && (
                        <div style={{ marginTop: 4 }}>
                          <a href={editingVehicle.documents_detail.insurance.document_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                            📄 Current Uploaded Insurance
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Pollution Certificate (PUC) */}
                    <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        Pollution Certificate (PUC) Expiry & File
                      </label>
                      <input
                        type="date"
                        value={formData.pollution_expiry}
                        onChange={e => setFormData({ ...formData, pollution_expiry: e.target.value })}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 8 }}
                      />
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={e => setDocFiles({ ...docFiles, pollution_document: e.target.files[0] })}
                        style={{ fontSize: 11, width: '100%' }}
                      />
                      {editingVehicle?.documents_detail?.pollution?.document_url && !docFiles.pollution_document && (
                        <div style={{ marginTop: 4 }}>
                          <a href={editingVehicle.documents_detail.pollution.document_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                            📄 Current Uploaded PUC Certificate
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Route / Transport Permit */}
                    <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        Route / Transport Permit Expiry & File
                      </label>
                      <input
                        type="date"
                        value={formData.permit_expiry}
                        onChange={e => setFormData({ ...formData, permit_expiry: e.target.value })}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 8 }}
                      />
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={e => setDocFiles({ ...docFiles, permit_document: e.target.files[0] })}
                        style={{ fontSize: 11, width: '100%' }}
                      />
                      {editingVehicle?.documents_detail?.permit?.document_url && !docFiles.permit_document && (
                        <div style={{ marginTop: 4 }}>
                          <a href={editingVehicle.documents_detail.permit.document_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                            📄 Current Uploaded Permit
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="tm-btn-primary"
                    style={{ opacity: formSubmitting ? 0.7 : 1 }}
                  >
                    {formSubmitting ? 'Saving...' : editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── REVIEW DRIVER DOCUMENTS MODAL ─── */}
        {showVerifyModal && selectedDriverProfile && (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="modal-content" style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 750, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', pb: 12, marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                    🔍 Review & Verify Driver Professional Documents
                  </h3>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    Driver: <strong>{selectedDriverProfile.full_name}</strong> ({selectedDriverProfile.employee_id})
                  </div>
                </div>
                <button onClick={() => setShowVerifyModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                  Rejection Reason (required if rejecting document)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Image blurry, licence expired..."
                  value={rejectionReasonInput}
                  onChange={e => setRejectionReasonInput(e.target.value)}
                  style={{ width: '100%', padding: '7px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
                {/* Licence Card */}
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>🪪 Driving Licence</h4>
                    <span className={`badge ${selectedDriverProfile.licence_verification_status === 'VERIFIED' ? 'badge-green' : selectedDriverProfile.licence_verification_status === 'REJECTED' ? 'badge-red' : 'badge-yellow'}`}>
                      {selectedDriverProfile.licence_verification_status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                    Licence No: <strong>{selectedDriverProfile.licence_number || 'N/A'}</strong> &bull; Expiry: <strong>{selectedDriverProfile.licence_expiry_date || 'N/A'}</strong>
                  </div>
                  {selectedDriverProfile.licence_document ? (
                    <div style={{ marginBottom: 12 }}>
                      <a href={selectedDriverProfile.licence_document} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>
                        📄 View Licence Document Payload
                      </a>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>No document file uploaded.</div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleVerifyDocument('LICENCE', 'VERIFY')}
                      style={{ padding: '6px 14px', borderRadius: 6, background: '#166534', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                    >
                      ✓ Verify Licence
                    </button>
                    <button
                      onClick={() => handleVerifyDocument('LICENCE', 'REJECT')}
                      style={{ padding: '6px 14px', borderRadius: 6, background: '#991b1b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                    >
                      ✕ Reject Licence
                    </button>
                  </div>
                </div>

                {/* Medical Fitness Card */}
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>🩺 Medical Fitness Certificate</h4>
                    <span className={`badge ${selectedDriverProfile.medical_verification_status === 'VERIFIED' ? 'badge-green' : selectedDriverProfile.medical_verification_status === 'REJECTED' ? 'badge-red' : 'badge-yellow'}`}>
                      {selectedDriverProfile.medical_verification_status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                    Expiry: <strong>{selectedDriverProfile.medical_expiry_date || 'N/A'}</strong>
                  </div>
                  {selectedDriverProfile.medical_document ? (
                    <div style={{ marginBottom: 12 }}>
                      <a href={selectedDriverProfile.medical_document} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>
                        📄 View Medical Certificate Payload
                      </a>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>No document file uploaded.</div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleVerifyDocument('MEDICAL', 'VERIFY')}
                      style={{ padding: '6px 14px', borderRadius: 6, background: '#166534', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                    >
                      ✓ Verify Medical
                    </button>
                    <button
                      onClick={() => handleVerifyDocument('MEDICAL', 'REJECT')}
                      style={{ padding: '6px 14px', borderRadius: 6, background: '#991b1b', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                    >
                      ✕ Reject Medical
                    </button>
                  </div>
                </div>

                {/* Approved Permitted Vehicle Types Selection */}
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>
                    🚍 Approved Permitted Vehicle Types
                  </h4>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    {VEHICLE_TYPE_OPTIONS.map(vt => {
                      const isChecked = permittedTypesInput.includes(vt.value);
                      return (
                        <label key={vt.value} style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e => {
                              if (e.target.checked) {
                                setPermittedTypesInput([...permittedTypesInput, vt.value]);
                              } else {
                                setPermittedTypesInput(permittedTypesInput.filter(x => x !== vt.value));
                              }
                            }}
                          />
                          {vt.label}
                        </label>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handleVerifyDocument('PERMITTED_TYPES', 'VERIFY')}
                    className="tm-btn-primary"
                    style={{ fontSize: 12, padding: '6px 14px' }}
                  >
                    Save Permitted Vehicle Types
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowVerifyModal(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13, fontWeight: 600 }}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── ASSIGN DRIVER MODAL ─── */}
        {showAssignModal && assignTargetVehicle && (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="modal-content" style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', pb: 12, marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                    🧑‍✈️ Assign Driver to {assignTargetVehicle.registration_number}
                  </h3>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    Vehicle Type: <strong>{assignTargetVehicle.vehicle_type_display || assignTargetVehicle.vehicle_type}</strong>
                  </div>
                </div>
                <button onClick={() => setShowAssignModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
              </div>

              {loadingEligibleDrivers ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Filtering eligible drivers...</div>
              ) : (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                      Select Eligible Driver (Permitted for {assignTargetVehicle.vehicle_type}) *
                    </label>
                    {eligibleDrivers.length === 0 ? (
                      <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
                        ⚠️ No eligible drivers available for category <strong>{assignTargetVehicle.vehicle_type}</strong>. Ensure drivers are approved, fully verified, and permitted for this vehicle type.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {eligibleDrivers.map(ed => (
                          <label key={ed.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: selectedDriverToAssign === ed.id ? '1px solid #2563eb' : '1px solid #cbd5e1', borderRadius: 8, background: selectedDriverToAssign === ed.id ? '#eff6ff' : '#fff', cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <input
                                type="radio"
                                name="eligible_driver"
                                value={ed.id}
                                checked={selectedDriverToAssign === ed.id}
                                onChange={() => setSelectedDriverToAssign(ed.id)}
                              />
                              <div>
                                <strong>{ed.full_name}</strong> ({ed.employee_id})
                                {ed.is_assigned && (
                                  <div style={{ fontSize: 11, color: '#b45309' }}>
                                    Currently assigned to: {ed.currently_assigned_vehicle}
                                  </div>
                                )}
                              </div>
                            </div>
                            <span className="badge badge-green" style={{ fontSize: 11 }}>Eligible</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
                    <button
                      onClick={() => handleUnassignDriver(assignTargetVehicle.id)}
                      style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      🚫 Unassign Driver
                    </button>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setShowAssignModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13 }}>
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmAssignment}
                        disabled={!selectedDriverToAssign}
                        className="tm-btn-primary"
                        style={{ opacity: selectedDriverToAssign ? 1 : 0.5 }}
                      >
                        Confirm Assignment
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ─── VEHICLE DETAILS MODAL ─── */}
        {showDetailModal && selectedVehicle && (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="modal-content" style={{ background: '#ffffff', borderRadius: 16, width: '95%', maxWidth: 1100, maxHeight: '92vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 12, marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                    🚘 {selectedVehicle.registration_number}
                  </h3>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    Vehicle ID: {selectedVehicle.vehicle_id || `VEH-${selectedVehicle.id}`} &bull; Current ODO: <strong>{selectedVehicle.odometer_reading || 0} KM</strong>
                  </div>
                </div>
                <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
              </div>

              {/* Sub-tab Navigation */}
              <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #cbd5e1', marginBottom: 20 }}>
                <button
                  onClick={() => setOdoDetailTab('specs')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px 8px 0 0',
                    border: 'none',
                    borderBottom: odoDetailTab === 'specs' ? '2px solid #2563eb' : '2px solid transparent',
                    background: odoDetailTab === 'specs' ? '#eff6ff' : 'transparent',
                    color: odoDetailTab === 'specs' ? '#1d4ed8' : '#64748b',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  ℹ️ Basic Specs & Documents
                </button>
                <button
                  onClick={() => {
                    setOdoDetailTab('odo');
                    fetchODOHistory(selectedVehicle.id, odoHistoryMovementFilter, odoFilterFromDate, odoFilterToDate);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px 8px 0 0',
                    border: 'none',
                    borderBottom: odoDetailTab === 'odo' ? '2px solid #2563eb' : '2px solid transparent',
                    background: odoDetailTab === 'odo' ? '#eff6ff' : 'transparent',
                    color: odoDetailTab === 'odo' ? '#1d4ed8' : '#64748b',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  📈 ODO Meter History & Logs
                </button>
              </div>

              {odoDetailTab === 'specs' ? (
                <>
                  <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#334155' }}>
                      Basic Specifications
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                      <div><span style={{ color: '#64748b' }}>Vehicle Type:</span> <strong>{selectedVehicle.vehicle_type_display || selectedVehicle.vehicle_type}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Make / Manufacturer:</span> <strong>{selectedVehicle.make}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Model:</span> <strong>{selectedVehicle.model}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Manufacturing Year:</span> <strong>{selectedVehicle.manufacturing_year}</strong></div>
                      <div><span style={{ color: '#64748b' }}>Seating Capacity:</span> <strong>{selectedVehicle.seating_capacity} Seats</strong></div>
                      <div><span style={{ color: '#64748b' }}>ODO Meter Reading:</span> <strong>📈 {(selectedVehicle.odometer_reading !== undefined && selectedVehicle.odometer_reading !== null) ? selectedVehicle.odometer_reading : (selectedVehicle.odo_meter_reading ?? 0)} km</strong></div>
                      <div><span style={{ color: '#64748b' }}>Current Status:</span> {getStatusBadge(selectedVehicle.status)}</div>
                    </div>
                  </div>

                  {/* Assigned Driver Section */}
                  <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#334155' }}>
                        🧑‍✈️ Currently Assigned Driver
                      </h4>
                      <button
                        onClick={() => {
                          setShowDetailModal(false);
                          handleOpenAssignModal(selectedVehicle);
                        }}
                        className="tm-btn-primary"
                        style={{ fontSize: 12, padding: '6px 12px' }}
                      >
                        {(selectedVehicle.assigned_driver || selectedVehicle.driver) ? '✏️ Edit Driver' : '🧑‍✈️ Assign Driver'}
                      </button>
                    </div>
                    {(selectedVehicle.assigned_driver || selectedVehicle.driver) ? (
                      (() => {
                        const drv = selectedVehicle.assigned_driver || selectedVehicle.driver;
                        return (
                          <div style={{ fontSize: 13, color: '#1e293b' }}>
                            <div><strong>Driver Name:</strong> {drv.name || drv.driver_name}</div>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                              Employee ID: <strong>{drv.employee_id || 'N/A'}</strong>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                        No driver currently assigned to this vehicle.
                      </div>
                    )}
                  </div>

                  {/* Assigned Route Section */}
                  <div style={{ background: '#eff6ff', borderRadius: 12, padding: 16, border: '1px solid #bfdbfe', marginBottom: 20 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: '#1e40af' }}>
                      🗺️ Assigned Transport Route
                    </h4>
                    {selectedVehicle.assigned_route ? (
                      <div style={{ fontSize: 13, color: '#1e293b' }}>
                        <div style={{ fontWeight: 700 }}>
                          Route: {selectedVehicle.assigned_route.route_number} — {selectedVehicle.assigned_route.route_name}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Start: {selectedVehicle.assigned_route.starting_point || 'Campus'} &bull; Status: {selectedVehicle.assigned_route.status}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        No route currently assigned to this vehicle.
                      </div>
                    )}
                  </div>

                  {/* Statutory Documents & Compliance Section */}
                  <div style={{ background: '#ffffff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                    <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                      📜 Statutory Vehicle Documents & Expiry Validation
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      {/* RC */}
                      {(() => {
                        const rcInfo = getDocumentStatusInfo(selectedVehicle.registration_expiry);
                        const docUrl = selectedVehicle.documents_detail?.registration?.document_url || selectedVehicle.registration_document;
                        return (
                          <div style={{ background: rcInfo.bg, border: `1px solid ${rcInfo.border}`, borderRadius: 10, padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <strong style={{ fontSize: 13, color: '#1e293b' }}>🪪 Registration (RC)</strong>
                              <span style={{ fontSize: 11, fontWeight: 700, color: rcInfo.color }}>{rcInfo.label}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                              Expiry Date: <strong>{selectedVehicle.registration_expiry || 'Not Specified'}</strong>
                            </div>
                            {docUrl ? (
                              <a href={docUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', fontWeight: 700, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                📄 View RC Document
                              </a>
                            ) : (
                              <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No RC File Uploaded</span>
                            )}
                          </div>
                        );
                      })()}

                      {/* Insurance */}
                      {(() => {
                        const insInfo = getDocumentStatusInfo(selectedVehicle.insurance_expiry);
                        const docUrl = selectedVehicle.documents_detail?.insurance?.document_url || selectedVehicle.insurance_document;
                        return (
                          <div style={{ background: insInfo.bg, border: `1px solid ${insInfo.border}`, borderRadius: 10, padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <strong style={{ fontSize: 13, color: '#1e293b' }}>🛡️ Insurance Policy</strong>
                              <span style={{ fontSize: 11, fontWeight: 700, color: insInfo.color }}>{insInfo.label}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                              Expiry Date: <strong>{selectedVehicle.insurance_expiry || 'Not Specified'}</strong>
                            </div>
                            {docUrl ? (
                              <a href={docUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', fontWeight: 700, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                📄 View Insurance Document
                              </a>
                            ) : (
                              <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No Insurance File Uploaded</span>
                            )}
                          </div>
                        );
                      })()}

                      {/* Pollution (PUC) */}
                      {(() => {
                        const pucInfo = getDocumentStatusInfo(selectedVehicle.pollution_expiry);
                        const docUrl = selectedVehicle.documents_detail?.pollution?.document_url || selectedVehicle.pollution_document;
                        return (
                          <div style={{ background: pucInfo.bg, border: `1px solid ${pucInfo.border}`, borderRadius: 10, padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <strong style={{ fontSize: 13, color: '#1e293b' }}>🌱 Pollution (PUC)</strong>
                              <span style={{ fontSize: 11, fontWeight: 700, color: pucInfo.color }}>{pucInfo.label}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                              Expiry Date: <strong>{selectedVehicle.pollution_expiry || 'Not Specified'}</strong>
                            </div>
                            {docUrl ? (
                              <a href={docUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', fontWeight: 700, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                📄 View PUC Certificate
                              </a>
                            ) : (
                              <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No PUC File Uploaded</span>
                            )}
                          </div>
                        );
                      })()}

                      {/* Permit */}
                      {(() => {
                        const pmtInfo = getDocumentStatusInfo(selectedVehicle.permit_expiry);
                        const docUrl = selectedVehicle.documents_detail?.permit?.document_url || selectedVehicle.permit_document;
                        return (
                          <div style={{ background: pmtInfo.bg, border: `1px solid ${pmtInfo.border}`, borderRadius: 10, padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <strong style={{ fontSize: 13, color: '#1e293b' }}>📜 Transport Permit</strong>
                              <span style={{ fontSize: 11, fontWeight: 700, color: pmtInfo.color }}>{pmtInfo.label}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                              Expiry Date: <strong>{selectedVehicle.permit_expiry || 'Not Specified'}</strong>
                            </div>
                            {docUrl ? (
                              <a href={docUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', fontWeight: 700, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                📄 View Permit Document
                              </a>
                            ) : (
                              <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No Permit File Uploaded</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </>
              ) : (
                /* 📈 ODO METER INNER TAB */
                <div>
                  {loadingOdoHistory && !odoHistoryData ? (
                    <div style={{ textTransform: 'uppercase', textAlign: 'center', padding: '60px 0', color: '#64748b', fontSize: 13 }}>
                      ⏳ Loading ODO History & Metrics...
                    </div>
                  ) : (
                    <div>
                      {/* ─── DATE RANGE FILTER & ODO RECORDS ─── */}
                      {(() => {
                        const todayStr = getCurrentISTDateString();
                        const isTodayActive = odoFilterFromDate === todayStr && odoFilterToDate === todayStr;

                        const [istY, istM, istD] = todayStr.split('-').map(Number);
                        const monthStartStr = `${istY}-${String(istM).padStart(2, '0')}-01`;
                        const d7 = new Date(istY, istM - 1, istD - 6);
                        const past7Str = `${d7.getFullYear()}-${String(d7.getMonth() + 1).padStart(2, '0')}-${String(d7.getDate()).padStart(2, '0')}`;
                        const isLast7Active = odoFilterFromDate === past7Str && odoFilterToDate === todayStr;
                        const isThisMonthActive = odoFilterFromDate === monthStartStr && odoFilterToDate === todayStr;
                        const isAllTimeActive = !odoFilterFromDate && !odoFilterToDate && odoHistoryMovementFilter === 'ALL';

                        return (
                          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                              <div>
                                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span>📅</span> Vehicle ODO Records & Mileage Log
                                  {loadingOdoHistory && (
                                    <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                                      ⏳ Updating...
                                    </span>
                                  )}
                                </h4>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                  Select From Date and To Date to view distance driven and trip records during specific days.
                                </div>
                              </div>

                              {/* Quick Preset Buttons */}
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOdoFilterFromDate(todayStr);
                                    setOdoFilterToDate(todayStr);
                                    fetchODOHistory(selectedVehicle.id, odoHistoryMovementFilter, todayStr, todayStr);
                                  }}
                                  style={{
                                    padding: '5px 12px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    borderRadius: 6,
                                    border: isTodayActive ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                    background: isTodayActive ? '#2563eb' : '#ffffff',
                                    color: isTodayActive ? '#ffffff' : '#334155',
                                    cursor: 'pointer',
                                    boxShadow: isTodayActive ? '0 1px 2px rgba(37, 99, 235, 0.3)' : 'none',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  Today
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOdoFilterFromDate(past7Str);
                                    setOdoFilterToDate(todayStr);
                                    fetchODOHistory(selectedVehicle.id, odoHistoryMovementFilter, past7Str, todayStr);
                                  }}
                                  style={{
                                    padding: '5px 12px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    borderRadius: 6,
                                    border: isLast7Active ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                    background: isLast7Active ? '#2563eb' : '#ffffff',
                                    color: isLast7Active ? '#ffffff' : '#334155',
                                    cursor: 'pointer',
                                    boxShadow: isLast7Active ? '0 1px 2px rgba(37, 99, 235, 0.3)' : 'none',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  Last 7 Days
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOdoFilterFromDate(monthStartStr);
                                    setOdoFilterToDate(todayStr);
                                    fetchODOHistory(selectedVehicle.id, odoHistoryMovementFilter, monthStartStr, todayStr);
                                  }}
                                  style={{
                                    padding: '5px 12px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    borderRadius: 6,
                                    border: isThisMonthActive ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                    background: isThisMonthActive ? '#2563eb' : '#ffffff',
                                    color: isThisMonthActive ? '#ffffff' : '#334155',
                                    cursor: 'pointer',
                                    boxShadow: isThisMonthActive ? '0 1px 2px rgba(37, 99, 235, 0.3)' : 'none',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  This Month
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOdoFilterFromDate('');
                                    setOdoFilterToDate('');
                                    setOdoHistoryMovementFilter('ALL');
                                    fetchODOHistory(selectedVehicle.id, 'ALL', '', '');
                                  }}
                                  style={{
                                    padding: '5px 12px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    borderRadius: 6,
                                    border: isAllTimeActive ? '1px solid #475569' : '1px solid #cbd5e1',
                                    background: isAllTimeActive ? '#334155' : '#f1f5f9',
                                    color: isAllTimeActive ? '#ffffff' : '#64748b',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  All Time / Reset
                                </button>
                              </div>
                            </div>

                            {/* Filter Controls Row */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'flex-end' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                                  From Date
                                </label>
                                <input
                                  type="date"
                                  value={odoFilterFromDate}
                                  onChange={(e) => setOdoFilterFromDate(e.target.value)}
                                  className="tm-input"
                                  style={{ width: '100%', fontSize: 12, padding: '7px 10px' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                                  To Date
                                </label>
                                <input
                                  type="date"
                                  value={odoFilterToDate}
                                  onChange={(e) => setOdoFilterToDate(e.target.value)}
                                  className="tm-input"
                                  style={{ width: '100%', fontSize: 12, padding: '7px 10px' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                                  Movement Type
                                </label>
                                <select
                                  value={odoHistoryMovementFilter}
                                  onChange={(e) => setOdoHistoryMovementFilter(e.target.value)}
                                  className="tm-select"
                                  style={{ width: '100%', fontSize: 12, padding: '7px 10px' }}
                                >
                                  <option value="ALL">All Movements</option>
                                  <option value="ROUTE_DEPARTURE">Route Departure</option>
                                  <option value="ROUTE_ARRIVAL">Route Arrival</option>
                                  <option value="INTERIM_MOVEMENT">Interim Movements</option>
                                  <option value="INITIAL_ODO">Initial Baseline</option>
                                  <option value="CORRECTION">Authorized Corrections</option>
                                </select>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  type="button"
                                  disabled={loadingOdoHistory}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    fetchODOHistory(selectedVehicle.id, odoHistoryMovementFilter, odoFilterFromDate, odoFilterToDate);
                                  }}
                                  className="tm-btn-primary"
                                  style={{ flex: 1, padding: '8px 12px', fontSize: 12, opacity: loadingOdoHistory ? 0.7 : 1, cursor: loadingOdoHistory ? 'wait' : 'pointer' }}
                                >
                                  {loadingOdoHistory ? '⏳ Filtering...' : '🔍 Filter'}
                                </button>
                                {(odoFilterFromDate || odoFilterToDate || odoHistoryMovementFilter !== 'ALL') && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setOdoFilterFromDate('');
                                      setOdoFilterToDate('');
                                      setOdoHistoryMovementFilter('ALL');
                                      fetchODOHistory(selectedVehicle.id, 'ALL', '', '');
                                    }}
                                    style={{ padding: '8px 12px', fontSize: 12, borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', color: '#64748b', cursor: 'pointer' }}
                                    title="Clear date filters"
                                  >
                                    ✕ Clear
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Content Container with Smooth Opacity during background fetch */}
                      <div style={{ opacity: loadingOdoHistory ? 0.6 : 1, transition: 'opacity 0.2s ease', pointerEvents: loadingOdoHistory ? 'none' : 'auto' }}>

                      {/* ─── TOTAL KM DRIVEN IN SELECTED DAYS BANNER ─── */}
                      {(() => {
                        const records = odoHistoryData?.history || odoHistoryData?.records || [];
                        const totalKm = (odoHistoryData?.metrics?.range_total_km !== undefined)
                          ? odoHistoryData.metrics.range_total_km
                          : records.filter(r => ['COMPLETED', 'VERIFIED'].includes(r.status) || !r.status).reduce((s, r) => s + (Number(r.calculated_km ?? r.distance_covered_km) || 0), 0);
                        const routeKm = (odoHistoryData?.metrics?.range_route_km !== undefined)
                          ? odoHistoryData.metrics.range_route_km
                          : records.filter(r => r.movement_type === 'ROUTE_ARRIVAL' && (['COMPLETED', 'VERIFIED'].includes(r.status) || !r.status)).reduce((s, r) => s + (Number(r.calculated_km ?? r.distance_covered_km) || 0), 0);
                        const interimKm = (odoHistoryData?.metrics?.range_interim_km !== undefined)
                          ? odoHistoryData.metrics.range_interim_km
                          : records.filter(r => r.movement_type === 'INTERIM_MOVEMENT' && (['COMPLETED', 'VERIFIED'].includes(r.status) || !r.status)).reduce((s, r) => s + (Number(r.calculated_km ?? r.distance_covered_km) || 0), 0);

                        return (
                          <div style={{
                            background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                            borderRadius: 12,
                            padding: '16px 20px',
                            color: '#ffffff',
                            marginBottom: 16,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 16,
                            boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                          }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#bfdbfe', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                🚗 Total KM Driven During Selected Days
                              </div>
                              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                <span>{totalKm.toLocaleString()}</span>
                                <span style={{ fontSize: 15, fontWeight: 600, color: '#93c5fd' }}>KM</span>
                              </div>
                              <div style={{ fontSize: 11, color: '#e0e7ff', marginTop: 4 }}>
                                {odoFilterFromDate && odoFilterToDate ? (
                                  <>Range: <strong>{odoFilterFromDate}</strong> to <strong>{odoFilterToDate}</strong></>
                                ) : odoFilterFromDate ? (
                                  <>From: <strong>{odoFilterFromDate}</strong> onwards</>
                                ) : odoFilterToDate ? (
                                  <>Up to: <strong>{odoFilterToDate}</strong></>
                                ) : (
                                  <>Cumulative total across all recorded days</>
                                )}
                              </div>
                            </div>

                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                              <div style={{ background: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(4px)', borderRadius: 8, padding: '8px 14px', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                <div style={{ fontSize: 11, color: '#bfdbfe' }}>🛫 Route Distance</div>
                                <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{routeKm.toLocaleString()} KM</div>
                              </div>
                              <div style={{ background: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(4px)', borderRadius: 8, padding: '8px 14px', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                <div style={{ fontSize: 11, color: '#bfdbfe' }}>🚘 Interim Distance</div>
                                <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{interimKm.toLocaleString()} KM</div>
                              </div>
                              <div style={{ background: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(4px)', borderRadius: 8, padding: '8px 14px', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                <div style={{ fontSize: 11, color: '#bfdbfe' }}>📋 Total Records</div>
                                <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{records.length} logs</div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* ─── RECORDS TABLE ─── */}
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, textAlign: 'left' }}>
                          <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <tr>
                              <th style={{ padding: '10px 12px', color: '#475569' }}>Date / Time</th>
                              <th style={{ padding: '10px 12px', color: '#475569' }}>Type</th>
                              <th style={{ padding: '10px 12px', color: '#475569' }}>ODO Reading</th>
                              <th style={{ padding: '10px 12px', color: '#475569' }}>Distance Driven</th>
                              <th style={{ padding: '10px 12px', color: '#475569' }}>Notes / Purpose</th>
                              <th style={{ padding: '10px 12px', color: '#475569' }}>Logged By</th>
                              <th style={{ padding: '10px 12px', color: '#475569' }}>Docs</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const records = odoHistoryData?.history || odoHistoryData?.records || [];
                              if (records.length === 0) {
                                return (
                                  <tr>
                                    <td colSpan={7} style={{ padding: '30px 20px', textAlign: 'center', color: '#94a3b8' }}>
                                      No ODO records found for the selected date range.
                                    </td>
                                  </tr>
                                );
                              }
                              return records.map((rec) => {
                                const dist = Number(rec.calculated_km ?? rec.distance_covered_km) || 0;
                                const isDiscrepancy = rec.status === 'DISCREPANCY' || (rec.discrepancy_km && rec.discrepancy_km > 0);
                                const docUrl = rec.supporting_document_url || rec.document_url || rec.supporting_document;
                                const displayDate = rec.created_at
                                  ? new Date(rec.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                  : (rec.date ? `${rec.date} ${rec.time || ''}` : '-');

                                return (
                                  <tr key={rec.id} style={{ borderBottom: '1px solid #f1f5f9', background: isDiscrepancy ? '#fef2f2' : 'transparent' }}>
                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                      {displayDate}
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <span style={{
                                        padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                                        background: rec.movement_type === 'INITIAL_ODO' ? '#f3f4f6' :
                                                    rec.movement_type === 'ROUTE_DEPARTURE' ? '#eff6ff' :
                                                    rec.movement_type === 'ROUTE_ARRIVAL' ? '#f0fdf4' :
                                                    rec.movement_type === 'CORRECTION' ? '#fff7ed' : '#faf5ff',
                                        color: rec.movement_type === 'INITIAL_ODO' ? '#374151' :
                                               rec.movement_type === 'ROUTE_DEPARTURE' ? '#1d4ed8' :
                                               rec.movement_type === 'ROUTE_ARRIVAL' ? '#15803d' :
                                               rec.movement_type === 'CORRECTION' ? '#c2410c' : '#7e22ce'
                                      }}>
                                        {rec.movement_type_display || rec.movement_type}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                                      {rec.previous_verified_odo !== null && rec.previous_verified_odo !== undefined && rec.previous_verified_odo !== rec.odo_reading ? (
                                        <span>{rec.previous_verified_odo.toLocaleString()} ➔ </span>
                                      ) : rec.prev_odo_reading !== null && rec.prev_odo_reading !== undefined && rec.prev_odo_reading !== rec.odo_reading ? (
                                        <span>{rec.prev_odo_reading.toLocaleString()} ➔ </span>
                                      ) : null}
                                      <strong>{rec.odo_reading ? rec.odo_reading.toLocaleString() : 0} KM</strong>
                                    </td>
                                    <td style={{ padding: '8px 12px', fontWeight: 700, color: dist > 0 ? '#0f766e' : '#64748b' }}>
                                      {dist > 0 ? (
                                        <span style={{ background: '#f0fdf4', color: '#15803d', padding: '2px 8px', borderRadius: 4, border: '1px solid #bbf7d0', fontSize: 11 }}>
                                          +{dist.toLocaleString()} KM
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#334155' }}>
                                      {rec.remarks || rec.interim_reason_display || '-'}
                                      {rec.is_suspicious && (
                                        <span style={{ marginLeft: 6, color: '#dc2626', fontSize: 10, fontWeight: 700, background: '#fee2e2', padding: '1px 5px', borderRadius: 3 }}>
                                          ⚠️ SUSPICIOUS (&gt;500km)
                                        </span>
                                      )}
                                      {isDiscrepancy && (
                                        <span style={{ marginLeft: 6, color: '#b91c1c', fontSize: 10, fontWeight: 700, background: '#fee2e2', padding: '1px 5px', borderRadius: 3 }}>
                                          ⚠️ Discrepancy
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#64748b' }}>
                                      {rec.recorded_by_name || rec.driver_name || 'System'}
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>
                                      {docUrl ? (
                                        <a href={docUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                          📄 Doc
                                        </a>
                                      ) : '-'}
                                    </td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── RECORD DEPARTURE ODO MODAL ─── */}
        {showDepartureModal && departureTargetVehicle && (
          <div className="odo-modal-overlay" onClick={() => setShowDepartureModal(false)}>
            <div className="odo-modal-card" onClick={e => e.stopPropagation()}>
              <div className="odo-modal-header">
                <div className="odo-modal-title-wrap">
                  <div className="odo-modal-icon-badge odo-icon-departure">🛫</div>
                  <div>
                    <h3 className="odo-modal-title">Record Campus Departure ODO</h3>
                    <div className="odo-modal-subtitle">
                      Vehicle: <span className="tm-plate-badge" style={{ fontSize: 11, padding: '1px 6px', marginLeft: 4 }}>{departureTargetVehicle.registration_number}</span> &bull; {departureTargetVehicle.make} {departureTargetVehicle.model}
                    </div>
                  </div>
                </div>
                <button type="button" className="odo-modal-close-btn" onClick={() => setShowDepartureModal(false)} title="Close">✕</button>
              </div>

              <form onSubmit={handleRecordDepartureSubmit}>
                <div className="odo-modal-body">
                  {/* Vehicle Context Strip */}
                  <div className="odo-vehicle-context-strip">
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Last Verified Fleet Reading</div>
                      <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>
                        {departureTargetVehicle.assigned_route ? `Route: ${departureTargetVehicle.assigned_route.route_number || departureTargetVehicle.assigned_route.route_name}` : 'Campus Fleet'} &bull; {departureTargetVehicle.assigned_driver?.driver_name || departureTargetVehicle.driver?.driver_name || 'Driver Assigned'}
                      </div>
                    </div>
                    <div className="odo-digital-meter-display">
                      <span>📈</span>
                      <span>{(departureTargetVehicle.odometer_reading || 0).toLocaleString()} KM</span>
                    </div>
                  </div>

                  {discrepancyAlertData && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 14, marginBottom: 16, color: '#991b1b', fontSize: 13 }}>
                      <div style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>⚠️</span> Unexplained ODO Discrepancy Detected!
                      </div>
                      <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.5 }}>
                        Last Verified ODO was <strong>{discrepancyAlertData.last_verified_odo?.toLocaleString()} KM</strong>. Entered Departure ODO is <strong>{discrepancyAlertData.entered_departure_odo?.toLocaleString() || discrepancyAlertData.departure_odo?.toLocaleString()} KM</strong> (Unexplained gap of +{discrepancyAlertData.discrepancy_gap?.toLocaleString() || discrepancyAlertData.unexplained_gap_km?.toLocaleString()} KM).
                        A discrepancy audit record has been flagged. Please record the missing movement as Interim or submit an ODO Correction request.
                      </p>
                    </div>
                  )}

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Departure Odometer Reading <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div className="odo-input-container">
                      <input
                        type="number"
                        required
                        min="0"
                        value={departureOdoInput}
                        onChange={(e) => setDepartureOdoInput(e.target.value)}
                        className="tm-input odo-input-number"
                        placeholder={`e.g. ${departureTargetVehicle.odometer_reading || 0}`}
                        autoFocus
                      />
                      <span className="odo-input-suffix">KM</span>
                    </div>
                    {departureOdoInput !== '' && (
                      <div style={{ marginTop: 6 }}>
                        {Number(departureOdoInput) === (departureTargetVehicle.odometer_reading || 0) ? (
                          <span className="odo-delta-badge" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>
                            ✓ Reading matches current recorded odometer (+0 KM)
                          </span>
                        ) : Number(departureOdoInput) > (departureTargetVehicle.odometer_reading || 0) ? (
                          <span className="odo-delta-badge" style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' }}>
                            ⚠️ +{(Number(departureOdoInput) - (departureTargetVehicle.odometer_reading || 0)).toLocaleString()} KM discrepancy vs last recorded
                          </span>
                        ) : (
                          <span className="odo-delta-badge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                            ❌ Reading cannot be lower than current ODO ({departureTargetVehicle.odometer_reading || 0} KM)
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Pre-Departure Notes / Shift Remarks
                    </label>
                    <textarea
                      rows={2}
                      value={departureRemarksInput}
                      onChange={(e) => setDepartureRemarksInput(e.target.value)}
                      className="tm-textarea"
                      placeholder="e.g. Morning student pickup route shift departure on schedule..."
                    />
                  </div>
                </div>

                <div className="odo-modal-footer">
                  <button type="button" onClick={() => setShowDepartureModal(false)} className="odo-btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingDeparture}
                    className="odo-btn-submit"
                    style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}
                  >
                    {submittingDeparture ? 'Recording...' : '🛫 Confirm Departure ODO'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── RECORD ARRIVAL ODO MODAL ─── */}
        {showArrivalModal && arrivalTargetVehicle && (
          <div className="odo-modal-overlay" onClick={() => setShowArrivalModal(false)}>
            <div className="odo-modal-card" onClick={e => e.stopPropagation()}>
              <div className="odo-modal-header">
                <div className="odo-modal-title-wrap">
                  <div className="odo-modal-icon-badge odo-icon-arrival">🛬</div>
                  <div>
                    <h3 className="odo-modal-title">Record Campus Arrival ODO</h3>
                    <div className="odo-modal-subtitle">
                      Vehicle: <span className="tm-plate-badge" style={{ fontSize: 11, padding: '1px 6px', marginLeft: 4 }}>{arrivalTargetVehicle.registration_number}</span> &bull; {arrivalTargetVehicle.make} {arrivalTargetVehicle.model}
                    </div>
                  </div>
                </div>
                <button type="button" className="odo-modal-close-btn" onClick={() => setShowArrivalModal(false)} title="Close">✕</button>
              </div>

              <form onSubmit={handleRecordArrivalSubmit}>
                <div className="odo-modal-body">
                  {/* Trip Flow & Distance Calculation Card */}
                  {(() => {
                    const depOdo = arrivalTargetVehicle.last_departure_odo || arrivalTargetVehicle.odometer_reading || 0;
                    const arrVal = arrivalOdoInput !== '' ? Number(arrivalOdoInput) : null;
                    const tripKm = arrVal !== null ? arrVal - depOdo : null;
                    const isSuspicious = tripKm !== null && tripKm > 500;

                    return (
                      <div className="odo-trip-flow-card">
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase' }}>Trip Departure Baseline</div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                            🛫 {depOdo.toLocaleString()} KM
                          </div>
                        </div>

                        <div style={{ fontSize: 20, color: '#16a34a', fontWeight: 900 }}>➔</div>

                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase' }}>Calculated Route Distance</div>
                          {tripKm !== null && tripKm >= 0 ? (
                            <div style={{ fontSize: 15, fontWeight: 800, color: isSuspicious ? '#ea580c' : '#15803d', marginTop: 2 }}>
                              +{tripKm.toLocaleString()} KM {isSuspicious && '⚠️'}
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', marginTop: 2 }}>Enter arrival reading</div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Arrival Odometer Reading <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div className="odo-input-container">
                      <input
                        type="number"
                        required
                        min={arrivalTargetVehicle.last_departure_odo || arrivalTargetVehicle.odometer_reading || 0}
                        value={arrivalOdoInput}
                        onChange={(e) => setArrivalOdoInput(e.target.value)}
                        className="tm-input odo-input-number"
                        placeholder={`Must be >= ${arrivalTargetVehicle.last_departure_odo || arrivalTargetVehicle.odometer_reading || 0}`}
                        autoFocus
                      />
                      <span className="odo-input-suffix">KM</span>
                    </div>
                    {arrivalOdoInput !== '' && (
                      <div style={{ marginTop: 6 }}>
                        {Number(arrivalOdoInput) - (arrivalTargetVehicle.last_departure_odo || arrivalTargetVehicle.odometer_reading || 0) > 500 ? (
                          <span className="odo-delta-badge" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>
                            🚨 Trip distance exceeds 500 KM threshold (will be flagged for audit review)
                          </span>
                        ) : Number(arrivalOdoInput) >= (arrivalTargetVehicle.last_departure_odo || arrivalTargetVehicle.odometer_reading || 0) ? (
                          <span className="odo-delta-badge" style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>
                            ✓ Valid route arrival (+{(Number(arrivalOdoInput) - (arrivalTargetVehicle.last_departure_odo || arrivalTargetVehicle.odometer_reading || 0)).toLocaleString()} KM added to fleet odometer)
                          </span>
                        ) : (
                          <span className="odo-delta-badge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                            ❌ Arrival reading cannot be lower than departure ODO ({arrivalTargetVehicle.last_departure_odo || arrivalTargetVehicle.odometer_reading || 0} KM)
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Arrival Notes / Route Completion Details
                    </label>
                    <textarea
                      rows={2}
                      value={arrivalRemarksInput}
                      onChange={(e) => setArrivalRemarksInput(e.target.value)}
                      className="tm-textarea"
                      placeholder="e.g. Evening route completed on schedule, passengers safely dropped..."
                    />
                  </div>
                </div>

                <div className="odo-modal-footer">
                  <button type="button" onClick={() => setShowArrivalModal(false)} className="odo-btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingArrival}
                    className="odo-btn-submit"
                    style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}
                  >
                    {submittingArrival ? 'Recording...' : '🛬 Confirm Arrival ODO'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── RECORD INTERIM MOVEMENT MODAL ─── */}
        {showInterimModal && interimTargetVehicle && (
          <div className="odo-modal-overlay" onClick={() => setShowInterimModal(false)}>
            <div className="odo-modal-card" onClick={e => e.stopPropagation()}>
              <div className="odo-modal-header">
                <div className="odo-modal-title-wrap">
                  <div className="odo-modal-icon-badge odo-icon-interim">🚘</div>
                  <div>
                    <h3 className="odo-modal-title">Record Interim / Non-Route Movement</h3>
                    <div className="odo-modal-subtitle">
                      Vehicle: <span className="tm-plate-badge" style={{ fontSize: 11, padding: '1px 6px', marginLeft: 4 }}>{interimTargetVehicle.registration_number}</span> &bull; {interimTargetVehicle.make} {interimTargetVehicle.model}
                    </div>
                  </div>
                </div>
                <button type="button" className="odo-modal-close-btn" onClick={() => setShowInterimModal(false)} title="Close">✕</button>
              </div>

              <form onSubmit={handleRecordInterimSubmit}>
                <div className="odo-modal-body">
                  {/* Context Strip */}
                  <div className="odo-vehicle-context-strip">
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Current Fleet Baseline ODO</div>
                      <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>Non-route mileage logged separately from passenger routes</div>
                    </div>
                    <div className="odo-digital-meter-display">
                      <span>📈</span>
                      <span>{(interimTargetVehicle.odometer_reading || 0).toLocaleString()} KM</span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Movement Reason <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                      value={interimReasonChoice}
                      onChange={(e) => setInterimReasonChoice(e.target.value)}
                      className="tm-select"
                    >
                      <option value="VEHICLE_RELOCATION">🔄 Vehicle Relocation / Parking Shift</option>
                      <option value="EMERGENCY_MOVEMENT">🚨 Emergency Movement</option>
                      <option value="WORKSHOP_SERVICE">🔧 Workshop / Maintenance Service</option>
                      <option value="FUEL_STATION">⛽ Fuel Station Refueling</option>
                      <option value="BREAKDOWN_RECOVERY">🛠️ Breakdown / Recovery</option>
                      <option value="OTHER">📝 Other (Mandatory explanation)</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      New End Odometer Reading <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div className="odo-input-container">
                      <input
                        type="number"
                        required
                        min={interimTargetVehicle.odometer_reading || 0}
                        value={interimOdoInput}
                        onChange={(e) => setInterimOdoInput(e.target.value)}
                        className="tm-input odo-input-number"
                        placeholder={`Must be >= ${interimTargetVehicle.odometer_reading || 0}`}
                        autoFocus
                      />
                      <span className="odo-input-suffix">KM</span>
                    </div>
                    {interimOdoInput !== '' && (
                      <div style={{ marginTop: 6 }}>
                        {Number(interimOdoInput) >= (interimTargetVehicle.odometer_reading || 0) ? (
                          <span className="odo-delta-badge" style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' }}>
                            🚘 +{(Number(interimOdoInput) - (interimTargetVehicle.odometer_reading || 0)).toLocaleString()} KM interim distance logged
                          </span>
                        ) : (
                          <span className="odo-delta-badge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                            ❌ Reading cannot be lower than current ODO ({interimTargetVehicle.odometer_reading || 0} KM)
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Reason & Purpose Details <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                      rows={2}
                      required
                      value={interimRemarksInput}
                      onChange={(e) => setInterimRemarksInput(e.target.value)}
                      className="tm-textarea"
                      placeholder="Provide specific details about destination, authorized purpose, or driver..."
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Supporting Document <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}>(Fuel Slip / Gate Pass / Work Order)</span>
                    </label>
                    <input
                      type="file"
                      onChange={(e) => setInterimDocFile(e.target.files[0])}
                      className="tm-input"
                      accept="image/*,.pdf"
                    />
                  </div>
                </div>

                <div className="odo-modal-footer">
                  <button type="button" onClick={() => setShowInterimModal(false)} className="odo-btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingInterim}
                    className="odo-btn-submit"
                    style={{ background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' }}
                  >
                    {submittingInterim ? 'Logging...' : '🚘 Log Interim Movement'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── SUBMIT CORRECTION REQUEST MODAL ─── */}
        {showCorrectionModal && correctionTargetVehicle && (
          <div className="odo-modal-overlay" onClick={() => setShowCorrectionModal(false)}>
            <div className="odo-modal-card" onClick={e => e.stopPropagation()}>
              <div className="odo-modal-header">
                <div className="odo-modal-title-wrap">
                  <div className="odo-modal-icon-badge odo-icon-correction">🛡️</div>
                  <div>
                    <h3 className="odo-modal-title">Request ODO Correction / Calibration</h3>
                    <div className="odo-modal-subtitle">
                      Vehicle: <span className="tm-plate-badge" style={{ fontSize: 11, padding: '1px 6px', marginLeft: 4 }}>{correctionTargetVehicle.registration_number}</span>
                    </div>
                  </div>
                </div>
                <button type="button" className="odo-modal-close-btn" onClick={() => setShowCorrectionModal(false)} title="Close">✕</button>
              </div>

              <form onSubmit={handleSubmitCorrectionSubmit}>
                <div className="odo-modal-body">
                  {/* Governance Alert */}
                  <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 12, color: '#9a3412', lineHeight: 1.5 }}>
                    <strong>🛡️ Governance & Audit Policy:</strong> Direct editing of historical movements is forbidden. Any manual odometer adjustments require mandatory justification, evidence attachment, and approval by an authorized Administrator.
                  </div>

                  {/* Comparison Strip */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Current Recorded ODO</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
                        {(correctionTargetVehicle.odometer_reading || 0).toLocaleString()} KM
                      </div>
                    </div>
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase' }}>Proposed New ODO</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#1d4ed8', marginTop: 4 }}>
                        {proposedOdoInput ? `${Number(proposedOdoInput).toLocaleString()} KM` : '—'}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Proposed Correct ODO Reading (KM) <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div className="odo-input-container">
                      <input
                        type="number"
                        required
                        min="0"
                        value={proposedOdoInput}
                        onChange={(e) => setProposedOdoInput(e.target.value)}
                        className="tm-input odo-input-number"
                        placeholder="e.g. 25400"
                        autoFocus
                      />
                      <span className="odo-input-suffix">KM</span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Correction Reason & Justification <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                      rows={3}
                      required
                      value={correctionReasonInput}
                      onChange={(e) => setCorrectionReasonInput(e.target.value)}
                      className="tm-textarea"
                      placeholder="Explain in detail why this correction is required (e.g. physical cluster replaced under warranty, data entry typo on departure)..."
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Supporting Document / Photo Evidence <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}>(Odometer Photo / Service Invoice)</span>
                    </label>
                    <input
                      type="file"
                      onChange={(e) => setCorrectionDocFile(e.target.files[0])}
                      className="tm-input"
                      accept="image/*,.pdf"
                    />
                  </div>
                </div>

                <div className="odo-modal-footer">
                  <button type="button" onClick={() => setShowCorrectionModal(false)} className="odo-btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingCorrection}
                    className="odo-btn-submit"
                    style={{ background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)' }}
                  >
                    {submittingCorrection ? 'Submitting...' : '🛡️ Submit Request to Admin'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── SET BASELINE ODO MODAL ─── */}
        {showBaselineModal && baselineTargetVehicle && (
          <div className="odo-modal-overlay" onClick={() => setShowBaselineModal(false)}>
            <div className="odo-modal-card" onClick={e => e.stopPropagation()}>
              <div className="odo-modal-header">
                <div className="odo-modal-title-wrap">
                  <div className="odo-modal-icon-badge odo-icon-baseline">⚙️</div>
                  <div>
                    <h3 className="odo-modal-title">Set Starting Baseline ODO</h3>
                    <div className="odo-modal-subtitle">
                      Vehicle: <span className="tm-plate-badge" style={{ fontSize: 11, padding: '1px 6px', marginLeft: 4 }}>{baselineTargetVehicle.registration_number}</span> &bull; {baselineTargetVehicle.make} {baselineTargetVehicle.model}
                    </div>
                  </div>
                </div>
                <button type="button" className="odo-modal-close-btn" onClick={() => setShowBaselineModal(false)} title="Close">✕</button>
              </div>

              <form onSubmit={handleSetBaselineSubmit}>
                <div className="odo-modal-body">
                  <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 12, color: '#5b21b6', lineHeight: 1.5 }}>
                    <strong>ℹ️ Fleet Baseline Setup:</strong> This vehicle currently has no starting odometer reading recorded (0 KM). Setting this baseline establishes the initial reference point from which all daily departures, arrivals, and interim movements will be tracked.
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Starting Baseline Odometer Reading <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div className="odo-input-container">
                      <input
                        type="number"
                        required
                        min="0"
                        value={baselineOdoInput}
                        onChange={(e) => setBaselineOdoInput(e.target.value)}
                        className="tm-input odo-input-number"
                        placeholder="e.g. 24500"
                        autoFocus
                      />
                      <span className="odo-input-suffix">KM</span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Remarks / Calibration Note
                    </label>
                    <textarea
                      rows={2}
                      value={baselineRemarksInput}
                      onChange={(e) => setBaselineRemarksInput(e.target.value)}
                      className="tm-textarea"
                      placeholder="e.g. Physical odometer reading verified by Transport Manager..."
                    />
                  </div>
                </div>

                <div className="odo-modal-footer">
                  <button type="button" onClick={() => setShowBaselineModal(false)} className="odo-btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingBaseline}
                    className="odo-btn-submit"
                    style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}
                  >
                    {submittingBaseline ? 'Initializing...' : '⚙️ Initialize Baseline ODO'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── ADMIN REVIEW CORRECTION MODAL ─── */}
        {selectedCorrectionForReview && (
          <div className="odo-modal-overlay" onClick={() => setSelectedCorrectionForReview(null)}>
            <div className="odo-modal-card" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
              <div className="odo-modal-header">
                <div className="odo-modal-title-wrap">
                  <div className="odo-modal-icon-badge" style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', color: '#334155' }}>⚖️</div>
                  <div>
                    <h3 className="odo-modal-title">Admin Review &mdash; ODO Correction #{selectedCorrectionForReview.id}</h3>
                    <div className="odo-modal-subtitle">Authorized verification and calibration decision</div>
                  </div>
                </div>
                <button type="button" className="odo-modal-close-btn" onClick={() => setSelectedCorrectionForReview(null)} title="Close">✕</button>
              </div>

              <form onSubmit={handleReviewCorrectionSubmit}>
                <div className="odo-modal-body">
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div>Vehicle: <strong>{selectedCorrectionForReview.vehicle_number}</strong></div>
                      <div>Requested By: <strong>{selectedCorrectionForReview.requested_by_name}</strong></div>
                      <div>Current ODO: <strong>{selectedCorrectionForReview.current_odo?.toLocaleString()} KM</strong></div>
                      <div>Proposed ODO: <strong style={{ color: '#c2410c' }}>{selectedCorrectionForReview.proposed_odo?.toLocaleString()} KM</strong></div>
                    </div>
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 8 }}>
                      <strong style={{ fontSize: 12, color: '#64748b' }}>Justification Reason:</strong>
                      <div style={{ color: '#0f172a', marginTop: 3, fontWeight: 500 }}>{selectedCorrectionForReview.reason}</div>
                    </div>
                    {selectedCorrectionForReview.document_url && (
                      <div style={{ marginTop: 10 }}>
                        <a href={selectedCorrectionForReview.document_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>
                          📄 View Uploaded Evidence Document
                        </a>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Review Action <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                      value={reviewActionChoice}
                      onChange={(e) => setReviewActionChoice(e.target.value)}
                      className="tm-select"
                    >
                      <option value="APPROVE">✅ Approve Correction & Update Vehicle ODO</option>
                      <option value="REJECT">❌ Reject Correction Request</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      Reviewer Notes / Decision Remarks
                    </label>
                    <textarea
                      rows={2}
                      value={reviewRemarksInput}
                      onChange={(e) => setReviewRemarksInput(e.target.value)}
                      className="tm-textarea"
                      placeholder="Enter audit approval remarks..."
                    />
                  </div>
                </div>

                <div className="odo-modal-footer">
                  <button type="button" onClick={() => setSelectedCorrectionForReview(null)} className="odo-btn-cancel">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingReview}
                    className="odo-btn-submit"
                    style={{ background: reviewActionChoice === 'APPROVE' ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}
                  >
                    {submittingReview ? 'Processing...' : reviewActionChoice === 'APPROVE' ? 'Confirm Approval' : 'Confirm Rejection'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TransportDashboard;